import React, { useState, useEffect, useRef } from "react";
import { FileText, Printer, Send, Download } from "lucide-react";
import { getSystemConfig } from "../services/systemConfigService";
import { getRentalProfiling } from "../services/rentalProfilingService";
import { fetchMechanicsData } from "../services/mechanicsService";
import html2pdf from "html2pdf.js";
import { PDFDocument } from "pdf-lib";

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inputCls =
  "border border-gray-400 rounded px-1.5 py-0.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";
const inlineInput = (extraCls = "") => `${inputCls} inline-block ${extraCls}`;

const pdfBox = (val, { minW = 120, flex = false, bold = false, multiline = false } = {}) => ({
  display: flex ? "block" : "inline-block",
  border: "1px solid #9ca3af",
  borderRadius: 4,
  background: "#f8fafc",
  padding: multiline ? "6px 10px" : "2px 8px",
  minWidth: flex ? "100%" : minW,
  minHeight: multiline ? 48 : 26,
  fontSize: 13,
  fontWeight: bold ? 600 : 400,
  color: val ? "#111827" : "#9ca3af",
  fontFamily: "Arial, Helvetica, sans-serif",
  whiteSpace: multiline ? "pre-wrap" : "normal",
  overflowWrap: "anywhere",   // ← add this
  wordBreak: "break-word",    // ← and this
  verticalAlign: "middle",
  lineHeight: multiline ? 1.6 : "22px",
  boxSizing: "border-box",
});

// ─── Date helpers ─────────────────────────────────────────────────────────────
const ddmmyyyyToISO = (str) => {
  if (!str) return "";
  const parts = str.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};
const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};
const formatToDDMMYYYY = (date) => {
  if (!date) return "";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) return date;
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}/${month}/${year}`;
};

// ─── DatePicker ───────────────────────────────────────────────────────────────
const DatePicker = ({ value, onChange, placeholder = "dd/mm/yyyy", className = "", readOnly = false, isPdfMode = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const inputRef = useRef(null);
  const isoValue = ddmmyyyyToISO(value);
  if (isPdfMode) {
    return (
      <span style={pdfBox(value, { minW: 130 })}>
        {value || <span style={{ color: "#9ca3af" }}>dd/mm/yyyy</span>}
      </span>
    );
  }
  if (readOnly) {
    return <input className={`${inputCls} bg-gray-50 ${className}`} value={value} readOnly placeholder={placeholder} />;
  }
  return (
    <span className="relative inline-block" style={{ verticalAlign: "middle" }}>
      <input
        className={`${inputCls} pr-7 ${className}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowPicker(true)}
      />
      <button type="button" tabIndex={-1}
        onClick={() => { setShowPicker((s) => !s); setTimeout(() => inputRef.current?.showPicker?.(), 50); }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 focus:outline-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      <input ref={inputRef} type="date" value={isoValue}
        onChange={(e) => { onChange(isoToDDMMYYYY(e.target.value)); setShowPicker(false); }}
        onBlur={() => setShowPicker(false)}
        className="absolute inset-0 opacity-0 pointer-events-none w-full h-full" tabIndex={-1}
        style={{ zIndex: showPicker ? 10 : -1, pointerEvents: showPicker ? "auto" : "none" }}
      />
    </span>
  );
};

// ─── Shared layout helpers ────────────────────────────────────────────────────
const pdfDocStyle = {
  background: "#ffffff",
  padding: "44px 52px",
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: 13,
  color: "#1a1a1a",
  lineHeight: 1.7,
  maxWidth: 780,
  margin: "0 auto",
};

const FibreHeader = ({ isPdfMode }) => (
  <div style={isPdfMode ? { display: "flex", justifyContent: "center", marginBottom: 24 } : undefined}
    className={isPdfMode ? undefined : "flex justify-center mb-6"}>
    <img src="https://quoting-tool.fibretelecomsuk.co.uk/images/logo-vertical.jpg"
      alt="Fibre Telecoms UK"
      style={isPdfMode ? { width: 155, height: "auto", objectFit: "contain" } : undefined}
      className={isPdfMode ? undefined : "w-40 h-auto object-contain"}
      crossOrigin="anonymous" />
  </div>
);

const PdfHr = ({ style = {} }) => (
  <hr style={{ border: "none", borderTop: "1px solid #d1d5db", margin: "16px 0", ...style }} />
);

const SectionDivider = ({ title }) => (
  <div className="flex items-center gap-3 my-8">
    <div className="flex-1 h-px bg-gray-300" />
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest px-2">{title}</span>
    <div className="flex-1 h-px bg-gray-300" />
  </div>
);



// ─── Helper to build a readable term string ───────────────────────────────────
const termToWords = (months) => {
  if (!months) return "";
  const m = parseInt(months, 10);
  if (m === 12) return "Twelve (12)";
  if (m === 24) return "Twenty Four (24)";
  if (m === 36) return "Thirty Six (36)";
  if (m === 48) return "Forty Eight (48)";
  if (m === 60) return "Sixty (60)";
  if (m === 72) return "Seventy Two (72)";
  if (m === 84) return "Eighty Four (84)";
  return `${m}`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 1 — Cover / Customer Details & Agreement Terms
// ═══════════════════════════════════════════════════════════════════════════════
const COVER_IMAGE_SRC = "https://quoting-tool.fibretelecomsuk.co.uk/images/LEASE.jpg";

const CoverImagePage = ({ isPdfMode, imageSrc = COVER_IMAGE_SRC }) => {
  if (isPdfMode) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
         
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          src={imageSrc}
          alt="Cover"
          crossOrigin="anonymous"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </div>
    );
  }
 
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <img
        src={imageSrc}
        alt="Cover"
        crossOrigin="anonymous"
        className="w-full h-auto block"
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 1b — Customer Details & Agreement Terms (sits right after the cover image)
// ═══════════════════════════════════════════════════════════════════════════════
const CoverPage = ({ data, onChange, isPdfMode,isSupportUser  }) => {
  const Bx = ({ v, minW = 120, bold = false }) => (
    <span style={pdfBox(v, { minW, bold })}>
      {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>—</span>}
    </span>
  );

  const Field = ({ label, k, ph, multi = false, small = false, readOnly = false }) => (
    <div className="flex items-start gap-2 mb-2">
      <span className="text-xs text-gray-600 shrink-0 w-32 pt-1">{label}</span>
      {multi
        ? <textarea className={`${inputCls} flex-1 h-14 resize-none text-xs`} value={data[k] || ""} onChange={(e) => onChange(k, e.target.value)} placeholder={ph} readOnly={readOnly} />
        : <input className={`${inputCls} ${small ? "w-36" : "flex-1"} text-xs ${readOnly ? "bg-gray-50" : ""}`} value={data[k] || ""} onChange={(e) => onChange(k, e.target.value)} placeholder={ph} readOnly={readOnly} />
      }
    </div>
  );

  const customerFields = [
    { label: "Full name",        k: "fullName",    ph: "Company / individual name" },
    { label: "Contact name",     k: "contactName", ph: "Primary contact" },
    { label: "Address",          k: "address",     ph: "Registered address", multi: true },
    { label: "Postcode",         k: "postcode",    ph: "e.g. SO52 9LP", small: true },
    { label: "Email address",    k: "email",       ph: "email@example.com" },
    { label: "Telephone no.",    k: "telephone",   ph: "01234 567890" },
    { label: "Company reg. no.", k: "companyReg",  ph: "12345678", small: true },
  ];

  if (isPdfMode) {
    const cell = (extra = {}) => ({
      border: "1px solid #9ca3af",
      padding: "8px 10px",
      verticalAlign: "top",
      fontSize: 12,
      ...extra,
    });
    const hdr = (extra = {}) => ({
      ...cell(),
      fontWeight: 700,
      background: "#f3f4f6",
      fontSize: 12,
      ...extra,
    });

    return (
      <div style={pdfDocStyle}>
        
      <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Details of Customer</h2>
      {(() => {
        const addressLines = (data.address || "").split("\n");
        while (addressLines.length < 4) addressLines.push("");

        const FlexBx = ({ v, bold }) => (
          <span style={{ ...pdfBox(v, { minW: 0, bold }), display: "block", width: "100%", boxSizing: "border-box" }}>
            {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>—</span>}
          </span>
        );

        const labelStyle      = { width: 90,  flexShrink: 0, fontSize: 12, color: "#374151" };
        const hiddenLabel      = { ...labelStyle, visibility: "hidden" };
        const rightLabelStyle = { width: 100, flexShrink: 0, fontSize: 12, color: "#374151", textAlign: "right", paddingRight: 6 };
        const cellRow         = { display: "flex", alignItems: "center", gap: 8, minWidth: 0 };

        const gridRow = (left, right) => (
          <div style={{ display: "grid", gridTemplateColumns: "62% 38%", columnGap: 16, marginBottom: 7 }}>
            {left}
            {right}
          </div>
        );

      return (
        
        <div style={{ marginBottom: 4 }}>
          {/* Full name — spans full width */}
          <div style={{ ...cellRow, marginBottom: 7 }}>
            <span style={labelStyle}>Full name</span>
            <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.fullName} bold /></div>
          </div>

          {/* Contact name | Email address */}
          {gridRow(
            <div style={cellRow}>
              <span style={labelStyle}>Contact name</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.contactName} /></div>
            </div>,
            <div style={cellRow}>
              <span style={rightLabelStyle}>Email address</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.email} /></div>
            </div>
          )}

          {/* Address line 1 + 2 | Company reg. no. */}
          {gridRow(
            <div style={cellRow}>
              <span style={labelStyle}>Address</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={addressLines[0]} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={addressLines[1]} /></div>
            </div>,
            <div style={cellRow}>
              <span style={rightLabelStyle}>Company reg. no.</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.companyReg} /></div>
            </div>
          )}

          {/* Address line 3 (alone) | Telephone no. */}
          {gridRow(
            <div style={cellRow}>
              <span style={hiddenLabel}>Address</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={addressLines[2]} /></div>
              <div style={{ flex: 1, minWidth: 0 }} />
            </div>,
            <div style={cellRow}>
              <span style={rightLabelStyle}>Telephone no.</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.telephone} /></div>
            </div>
          )}

        
        {/* Address line 4 (alone) | Postcode */}
          {gridRow(
            <div style={cellRow}>
              <span style={hiddenLabel}>Address</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={addressLines[3]} /></div>
              <div style={{ flex: 1, minWidth: 0 }} />
            </div>,
            <div style={cellRow}>
              <span style={rightLabelStyle}>Postcode</span>
              <div style={{ flex: 1, minWidth: 0 }}><FlexBx v={data.postcode} /></div>
            </div>
          )}

        
        </div>
        );
    })()}

       

        <div style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "10px 14px", background: "#f8fafc", marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Hosted Telephony Agreement</p>
          <p style={{ fontSize: 11, color: "#374151", lineHeight: 1.7, marginBottom: 4 }}>
            This Schedule is entered into pursuant to and incorporates the terms and conditions set out on Pages 4 and 5 of this Agreement. This Schedule will be governed by and incorporate the terms and conditions, but in the event of conflict the terms of this Schedule shall prevail.
          </p>
          
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ ...hdr(), width: "50%", textAlign: "left" }}>
                Agreement Terms
                <span style={{ fontWeight: 400, fontSize: 11, color: "#6b7280", marginLeft: 12 }}>
                  These payments and/or charges will be invoiced by the Funder to the Customer on a monthly basis
                </span>
              </th>
              <th style={{ ...hdr(), width: "50%", textAlign: "left" }}>Service Provision</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cell()}>
                <span style={{ fontSize: 12 }}>This Agreement is for a Fixed Period <strong>(in words)</strong> of</span>{" "}
                <Bx v={data.fixedPeriodWords} minW={160} />{" "}months.
              </td>
              <td style={{ ...cell(), fontSize: 11, color: "#374151" }}>
                These charges will be invoiced by Fibre to the Customer
                <br />
                <span style={{ fontWeight: 700, fontSize: 11 }}>Non-Standard/One-off Charges £</span>{" "}
                <Bx v={data.oneOffCharges} minW={100} /> + VAT
              </td>
            </tr>
            <tr>
              <td style={cell()}>
                <p style={{ fontSize: 12, marginBottom: 4 }}>By signing this Agreement You agree to pay to the</p>
                <p style={{ fontSize: 12, marginBottom: 6 }}>
                  Funder: An Initial Rental Payment of £{" "}
                  <Bx v={data.initialRental} minW={110} bold /> + VAT
                </p>
                <p style={{ fontSize: 12, marginBottom: 4 }}>Followed by</p>
                <span style={{ fontSize: 12 }}>
                  <Bx v={data.periodicRentalWords} minW={180} />
                </span>
                <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>(Periodic Rental Payments in words)</span>
              </td>
              <td style={cell({ verticalAlign: "middle" })}>
                <p style={{ fontSize: 12, marginBottom: 8 }}>
                  <strong>(B) Recurring Charges</strong>&nbsp;&nbsp;£{" "}
                  <Bx v={data.recurringCharges} minW={110} bold /> + VAT
                </p>
              </td>
            </tr>
            <tr>
              <td style={cell()}>
                <p style={{ fontSize: 12 }}>
                  <strong>(A) Periodic Rental Payments £</strong>{" "}
                  <Bx v={data.periodicRental} minW={110} bold /> + VAT
                </p>
              </td>
              <td style={cell()}>
                <p style={{ fontSize: 12, fontWeight: 700 }}>
                  (A + B) = Total Rental Payable £{" "}
                  <Bx v={data.totalRental} minW={110} bold /> + VAT
                </p>
              </td>
            </tr>
            
          </tbody>
        </table>
        <p style={{ fontSize: 11, color: "#374151", marginBottom: 10, lineHeight: 1.65 }}> The Fixed Period commences, and the Initial Rental is payable on the Date of this Agreement.
                The Periodic Rental Payments are payable on such date in the first month of the Fixed Period
                as Fibre shall notify to the Customer in writing and monthly thereafter.</p>
               <p style={{ fontSize: 11, color: "#374151", marginBottom: 10, lineHeight: 1.65 }}> You will be charged for call charges outside of the provided monthly allowance, please see clause 12.5.
                The charges above will be invoiced on a{" "}
                <Bx v={data.invoiceBasis} minW={80} /> basis</p>

        

      
  
  <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Upgrade or Settlement of Existing Finance Agreement</h2>
  <p style={{ fontSize: 11, color: "#374151", marginBottom: 10, lineHeight: 1.65 }}>
    If you wish the proposed Agreement to include any amount needed to settle your liability under an existing agreement then you should insert details of the Finance Company and agreement number in the spaces provided below. By inserting those details you acknowledge that Fibre acts as your agent for the purpose of receiving those settlement monies and paying the Finance Company.
  </p>
  {[
    { label: "* Finance company that owns the existing Goods", k: "existingFinanceCompany" },
    { label: "* Agreement reference number",                   k: "existingAgreementRef" },
    { label: "* Amount needed to settle current agreement",    k: "settlementAmount" },
  ].map(({ label, k }) => (
    <div key={k} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 7 }}>
      <span style={{ minWidth: 300, fontSize: 11, color: "#374151", flexShrink: 0 }}>{label}</span>
      <Bx v={data[k]} minW={160} />
    </div>
  ))}


       

        <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Customer's Declaration and Signature</h2>
        <p style={{ fontSize: 11, color: "#1a1a1a", marginBottom: 14, lineHeight: 1.75 }}>
          The Customer requests Fibre to purchase the Equipment described on page 3 of this Agreement and offers to take it on rental for use in its business on the
          terms set out above and in the Agreement. The Customer warrants that the particulars given are true and that this form and the Agreement was complete when
          signed. The Customer confirms that it has read the terms and conditions on pages 4, 5 and 6 of this Agreement, <b>in particular the exclusions and limitations in
          clause 11 of the Agreement</b>, and on each other page of this Agreement and agrees if this application is accepted to observe them. The person signing this
          Agreement for the Customer warrants that the Customer has the power and capacity to enter into this Agreement and the signatory is authorised to sign and
          bind the Customer.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
          <div>
            <div className="signature-section" data-section="rental_page1b_sig1" style={{ border: "1px solid #d1d5db", padding: 10, marginBottom: 10, fontSize: 11 }}>
              <p style={{ fontWeight: 700, marginBottom: 6 }}>Signature 1</p>
              <div className="signature-field adobe-sig" style={{ border: "1px solid #d1d5db", height: 44, marginBottom: 8, background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: 11 }}>
               {!isSupportUser && "{{Sig1_es_:signer1:signature}}"}
              </div>
              <p style={{ fontSize: 10, color: "#6b7280", textAlign: "right", marginBottom: 6 }}>duly authorised</p>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                <span style={{ minWidth: 110 }}>Print Full Name 1</span>
                <Bx v={data.sig1Name} minW={140} />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                <span style={{ minWidth: 110 }}>Job Title 1</span>
                <Bx v={data.sig1JobTitle} minW={140} />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ minWidth: 110 }}>Date 1</span>
                <Bx v={data.sig1Date} minW={140} />
              </div>
            </div>
            <div className="signature-section" data-section="rental_page1b_sig2" style={{ border: "1px solid #d1d5db", padding: 10, fontSize: 11 }}>
              <p style={{ fontWeight: 700, marginBottom: 6 }}>Signature 2 (if applicable)</p>
              <div className="signature-field adobe-sig" style={{ border: "1px solid #d1d5db", height: 44, marginBottom: 8, background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: 11 }}>
                {!isSupportUser && "{{Sig2_es_:signer2:signature}}"}
              </div>
              <p style={{ fontSize: 10, color: "#6b7280", textAlign: "right", marginBottom: 6 }}>duly authorised</p>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                <span style={{ minWidth: 110 }}>Print Full Name 2 (if applicable)</span>
                <Bx v={data.sig2Name} minW={100} />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                <span style={{ minWidth: 110 }}>Job Title 2 (if applicable)</span>
                <Bx v={data.sig2JobTitle} minW={100} />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ minWidth: 110 }}>Date 2 (if applicable)</span>
                <Bx v={data.sig2Date} minW={100} />
              </div>
            </div>
            <p style={{ fontSize: 11, fontWeight: 700, marginTop: 8, color: "#1a1a1a" }}>
              Under this Agreement the goods do not become your property and you must not sell them.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ border: "1px solid #d1d5db", padding: 12, fontSize: 11, lineHeight: 1.65 }}>
              <p style={{ fontWeight: 700, marginBottom: 6, textTransform: "uppercase", fontSize: 11 }}>
                IMPORTANT – USE OF YOUR INFORMATION
              </p>
              <p style={{ color: "#374151", marginBottom: 8 }}>
                Fibre may contact you periodically by telephone and/or post to provide information regarding events, products, services and content that may be of interest to you, unless you advise us that you do not wish to receive marketing or market research from us.
              </p>
              <p style={{ color: "#374151", marginBottom: 8 }}>
                Please confirm if you are happy to receive marketing or market research from us by email or other electronic means by ticking the box below:
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 14, height: 14, border: "1px solid #374151", borderRadius: 2, background: data.marketingConsent ? "#1d4ed8" : "#fff", flexShrink: 0 }} />
                <span style={{ fontSize: 11 }}>I consent to receive marketing by email or other electronic means</span>
              </div>
              <p style={{ fontSize: 10, color: "#6b7280" }}>
                If you wish to stop receiving marketing or market research from Fibre please contact us via billing@fibretelecomsuk.co.uk
              </p>
            </div>
            <div style={{ border: "1px solid #d1d5db", padding: 12, fontSize: 11 }}>
              <p style={{ fontWeight: 700, marginBottom: 10 }}>Accepted by Fibre Telecoms UK Limited</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <span style={{ minWidth: 110 }}>Date of this Agreement</span>
                <Bx v={data.agreementDate} minW={130} />
              </div>
              <div className="signature-field adobe-sig" style={{ border: "1px solid #d1d5db", height: 36, marginTop: 8, background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: 11 }}>
                {!isSupportUser && "{{Sig3_es_:signer3:signature}}"}
              </div>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 9, color: "#9ca3af", textAlign: "center", marginTop: 20 }}>
          GDPR-MPS-MONTHLY-03/23
        </p>
      
      </div>
      
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm font-sans text-sm leading-relaxed space-y-5">
      

      <div>
        <h2 className="text-sm font-bold mb-3">Details of Customer</h2>
        {customerFields.map(({ label, k, ph, multi, small }) => (
          <Field key={k} label={label} k={k} ph={ph} multi={multi} small={small} />
        ))}
      </div>

      <div className="border border-blue-200 rounded p-4 bg-blue-50 text-xs text-blue-900 space-y-1.5">
        <p className="font-bold">Hosted Telephony Agreement</p>
        <p>This Schedule is entered into pursuant to and incorporates the terms and conditions set out on Pages 4 and 5 of this Agreement. This Schedule will be governed by and incorporate the terms and conditions, but in the event of conflict the terms of this Schedule shall prevail.</p>
         </div>

      <hr className="border-gray-300" />

    <div style={{ background: "#f3f4f6", borderTop: "1px solid #9ca3af", borderBottom: "1px solid #9ca3af" }}>
      {/* Header band — full width, NOT split into the 60/40 columns */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "12px 16px 8px", gap: 16 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0, whiteSpace: "nowrap" }}>Agreement Terms</h3>
        <span style={{ fontSize: 12, color: "#4b5563", textAlign: "right" }}>
          These payments and/or charges will be invoiced by the Funder to the Customer on a monthly basis
        </span>
      </div>

      <div style={{ position: "relative" }}>
        {/* divider starts here, below the header, down through the A/Total row */}
        <div style={{ position: "absolute", left: "60%", top: 0, bottom: 0, width: 1, background: "#9ca3af" }} />

        {/* Upper body */}
        <div style={{ display: "grid", gridTemplateColumns: "60% 40%", columnGap: 16, padding: "0 16px" }}>
          <div style={{ paddingRight: 16 }}>
            <p style={{ fontSize: 12, margin: "0 0 10px" }}>
              This Agreement is for a Fixed Period <strong>(in words)</strong> of{" "}
              <Bx v={data.fixedPeriodWords} minW={140} />{" "}months.
            </p>
            <p style={{ fontSize: 12, margin: "0 0 6px" }}>By signing this Agreement You agree to pay to the</p>
            <p style={{ fontSize: 12, margin: "0 0 6px" }}>
              Funder: An Initial Rental Payment of £ <Bx v={data.initialRental} minW={110} bold /> + VAT
            </p>
            <p style={{ fontSize: 12, margin: "0 0 4px" }}>Followed by</p>
            <p style={{ fontSize: 12, margin: 0 }}>
              <Bx v={data.periodicRentalWords} minW={180} />{" "}
              <span style={{ fontSize: 11, color: "#6b7280" }}>(Periodic Rental Payments in words)</span>
            </p>
          </div>

          <div style={{ paddingLeft: 16 }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, margin: "4px 0 8px" }}>Service Provision</h3>
            <p style={{ fontSize: 12, color: "#374151", margin: "0 0 4px" }}>
              These charges will be invoiced by Fibre to the Customer
            </p>
            <p style={{ fontSize: 12, margin: "0 0 8px" }}>
              <strong>Non-Standard/One-off Charges £</strong>{" "}
              <Bx v={data.oneOffCharges} minW={100} /> + VAT
            </p>
            <p style={{ fontSize: 12, margin: 0 }}>
              <strong>(B) Recurring Charges</strong> £{" "}
              <Bx v={data.recurringCharges} minW={110} bold /> + VAT
            </p>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #9ca3af", margin: "10px 0" }} />

        {/* (A) row | Total row */}
        <div style={{ display: "grid", gridTemplateColumns: "60% 40%", columnGap: 16, padding: "0 16px 12px" }}>
          <div style={{ paddingRight: 16 }}>
            <p style={{ fontSize: 12, margin: 0 }}>
              <strong>(A) Periodic Rental Payments £</strong>{" "}
              <Bx v={data.periodicRental} minW={110} bold /> + VAT
            </p>
          </div>
          <div style={{ paddingLeft: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
              (A + B) = Total Rental Payable £{" "}
              <Bx v={data.totalRental} minW={110} bold /> + VAT
            </p>
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #9ca3af" }} />

      {/* Footer paragraphs — full width, no divider */}
      <div style={{ display: "grid", gridTemplateColumns: "60% 40%", columnGap: 16, padding: "10px 16px" }}>
        <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6 }}>
          The Fixed Period commences, and the Initial Rental is payable on the Date of this Agreement.
          The Periodic Rental Payments are payable on such date in the first month of the Fixed Period
          as Fibre shall notify to the Customer in writing and monthly thereafter.
        </div>
        <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6 }}>
          You will be charged for call charges outside of the provided monthly allowance, please see clause 12.5.
          The charges above will be invoiced on a <Bx v={data.invoiceBasis} minW={80} /> basis
        </div>
      </div>
    </div>

      <hr className="border-gray-300" />

      <div>
        <h2 className="text-sm font-bold mb-2">Upgrade or Settlement of Existing Finance Agreement</h2>
        <p className="text-xs text-gray-500 mb-3">
          If you wish the proposed Agreement to include any amount needed to settle your liability under an existing agreement then insert details below. These boxes should be left blank if not applicable.
        </p>
        {[
          { label: "* Finance company that owns the existing Goods", k: "existingFinanceCompany" },
          { label: "* Agreement reference number",                   k: "existingAgreementRef" },
          { label: "* Amount needed to settle current agreement",    k: "settlementAmount" },
        ].map(({ label, k }) => (
          <div key={k} className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-600 shrink-0 w-72">{label}</span>
            <input className={`${inputCls} flex-1 text-xs`} value={data[k] || ""}
              onChange={(e) => onChange(k, e.target.value)} placeholder="Leave blank if not applicable" />
          </div>
        ))}
      </div>

      <hr className="border-gray-300" />

      <div>
        <h2 className="text-sm font-bold mb-2">Customer's Declaration and Signature</h2>
        <p className="text-xs text-gray-700 mb-4 leading-relaxed">
          The Customer requests Fibre to purchase the Equipment described on page 3 of this Agreement and offers to take it on rental for use in its business on the
          terms set out above and in the Agreement. The Customer warrants that the particulars given are true and that this form and the Agreement was complete when
          signed. The Customer confirms that it has read the terms and conditions on pages 4, 5 and 6 of this Agreement, in particular the exclusions and limitations in
          clause 11 of the Agreement, and on each other page of this Agreement and agrees if this application is accepted to observe them. The person signing this
          Agreement for the Customer warrants that the Customer has the power and capacity to enter into this Agreement and the signatory is authorised to sign and
          bind the Customer.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            {[
              { prefix: "sig1", label: "Signature 1", nameLbl: "Print Full Name 1", jobLbl: "Job Title 1", dateLbl: "Date 1", tag: "{{Sig1_es_:signer1:signature}}", section: "rental_page1b_sig1_edit" },
              { prefix: "sig2", label: "Signature 2 (if applicable)", nameLbl: "Print Full Name 2 (if applicable)", jobLbl: "Job Title 2 (if applicable)", dateLbl: "Date 2 (if applicable)", tag: "{{Sig2_es_:signer2:signature}}", section: "rental_page1b_sig2_edit" },
            ].map(({ prefix, label, nameLbl, jobLbl, dateLbl, tag, section }) => (
              <div key={prefix} className="border border-gray-300 p-3 text-xs space-y-2 signature-section" data-section={section}>
                <p className="font-semibold">{label}</p>
                <div className="signature-field adobe-sig border border-gray-200 bg-gray-50 h-10 flex items-center justify-center text-gray-400 text-[10px]">
                  {!isSupportUser && tag}
                </div>
                <p className="text-right text-gray-400 text-xs">duly authorised</p>
                <div className="flex items-center gap-2">
                  <span className="w-40 shrink-0">{nameLbl}</span>
                  <input className={`${inputCls} flex-1 text-xs`} value={data[`${prefix}Name`] || ""} onChange={(e) => onChange(`${prefix}Name`, e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-40 shrink-0">{jobLbl}</span>
                  <input className={`${inputCls} flex-1 text-xs`} value={data[`${prefix}JobTitle`] || ""} onChange={(e) => onChange(`${prefix}JobTitle`, e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-40 shrink-0">{dateLbl}</span>
                  <DatePicker value={data[`${prefix}Date`]} onChange={(v) => onChange(`${prefix}Date`, v)} className="flex-1" />
                </div>
              </div>
            ))}
            <p className="text-xs font-semibold text-gray-800">
              Under this Agreement the goods do not become your property and you must not sell them.
            </p>
          </div>
          <div className="space-y-3">
            <div className="border border-gray-300 p-3 text-xs space-y-2">
              <p className="font-bold uppercase tracking-wide">IMPORTANT – USE OF YOUR INFORMATION</p>
              <p className="text-gray-700 leading-relaxed">
                Fibre may contact you periodically by telephone and/or post to provide information regarding events, products, services and content that may be of interest to you, unless you advise us that you do not wish to receive marketing or market research from us.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Please confirm if you are happy to receive marketing or market research from us by email or other electronic means by ticking the box below:
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!data.marketingConsent} onChange={(e) => onChange("marketingConsent", e.target.checked)} className="rounded" />
                <span>I consent to receive marketing by email or other electronic means</span>
              </label>
              <p className="text-gray-500">
                If you wish to stop receiving marketing or market research from Fibre please contact us via billing@fibretelecomsuk.co.uk
              </p>
            </div>
            <div className="border border-gray-300 p-3 text-xs space-y-2 signature-section" data-section="rental_page1b_sig3_edit">
              <p className="font-bold">Accepted by Fibre Telecoms UK Limited</p>
              <div className="flex items-center gap-2">
                <span className="w-40 shrink-0">Date of this Agreement</span>
                <DatePicker value={data.agreementDate} onChange={(v) => onChange("agreementDate", v)} className="flex-1" />
              </div>
              <div className="signature-field adobe-sig border border-gray-200 bg-gray-50 h-8 mt-2 flex items-center justify-center text-gray-400 text-[10px]">
                {!isSupportUser && "{{Sig3_es_:signer3:signature}}"}
              </div>
            </div>
          </div>
        </div>
      </div>
       <p className="text-center text-[10px] text-gray-400 pt-2">
          GDPR-MPS-MONTHLY-03/23
        </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 2 — Equipment Schedule
// ═══════════════════════════════════════════════════════════════════════════════
const EquipmentSchedule = ({ data, onChange, isPdfMode,isSupportUser  }) => {
  const thStyle = { background: "#f3f4f6", padding: "6px 8px", border: "1px solid #d1d5db", fontWeight: 700, textAlign: "left", fontSize: 12 };
  const tdStyle = { padding: "5px 8px", border: "1px solid #d1d5db", fontSize: 12 };

  const renderTable = (title, rows, setRows, col4Label = "") => {
    const showCol4 = !!col4Label;
    if (isPdfMode) {
      return (
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{title}</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 90 }}>Site Postcode</th>
                <th style={{ ...thStyle, width: 70 }}>Quantity</th>
                <th style={thStyle}>Description</th>
                {showCol4 && <th style={{ ...thStyle, width: 80 }}>{col4Label}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{row.postcode || ""}</td>
                  <td style={tdStyle}>{row.quantity || ""}</td>
                  <td style={tdStyle}>{row.description || ""}</td>
                  {showCol4 && <td style={{ ...tdStyle, fontWeight: col4Label === "Rate £" ? 600 : 400 }}>{row.newUsed || ""}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="mb-5">
        <h3 className="text-sm font-bold mb-2">{title}</h3>
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2 text-left w-28">Site Postcode</th>
              <th className="border border-gray-300 p-2 text-left w-16">Qty</th>
              <th className="border border-gray-300 p-2 text-left">Description</th>
              {showCol4 && <th className="border border-gray-300 p-2 text-left w-24">{col4Label}</th>}
              <th className="border border-gray-300 p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="border border-gray-300 p-1">
                  <input className={`${inputCls} w-full text-xs`} value={row.postcode}
                    onChange={(e) => { const r = [...rows]; r[i] = { ...r[i], postcode: e.target.value }; setRows(r); }} />
                </td>
                <td className="border border-gray-300 p-1">
                  <input className={`${inputCls} w-full text-xs`} value={row.quantity}
                    onChange={(e) => { const r = [...rows]; r[i] = { ...r[i], quantity: e.target.value }; setRows(r); }} />
                </td>
                <td className="border border-gray-300 p-1">
                  <input className={`${inputCls} w-full text-xs`} value={row.description}
                    onChange={(e) => { const r = [...rows]; r[i] = { ...r[i], description: e.target.value }; setRows(r); }} />
                </td>
                {showCol4 && (
                  <td className="border border-gray-300 p-1">
                    {col4Label === "New/Used"
                      ? <select className={`${inputCls} w-full text-xs`} value={row.newUsed}
                          onChange={(e) => { const r = [...rows]; r[i] = { ...r[i], newUsed: e.target.value }; setRows(r); }}>
                          <option value="">—</option><option value="New">New</option><option value="Used">Used</option>
                        </select>
                      : <input className={`${inputCls} w-full text-xs ${col4Label === "Rate £" ? "bg-gray-50" : ""}`}
                          value={row.newUsed}
                          readOnly={col4Label === "Rate £"}
                          onChange={(e) => { const r = [...rows]; r[i] = { ...r[i], newUsed: e.target.value }; setRows(r); }} />
                    }
                  </td>
                )}
                <td className="border border-gray-300 p-1 text-center">
                  <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button"
          onClick={() => setRows([...rows, { postcode: "", quantity: "", description: "", newUsed: "" }])}
          className="text-xs text-blue-600 hover:text-blue-800 mt-1">+ Add row</button>
      </div>
    );
  };

  if (isPdfMode) {
    return (
      <div style={pdfDocStyle}>
        {renderTable("Equipment", data.equipmentRows, null, "New/Used")}
        <PdfHr />
        <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Service Provision</p>
        {renderTable("Non Standard / One Off Charges", data.oneOffRows, null, "")}
        {renderTable("Recurring Charges", data.recurringRows, null, "Rate £")}
        <PdfHr />

        <div
          className="footer-block"
          style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
        >
          <div style={{ marginBottom: 24 }}>
  <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Additional provisions</p>
  <div style={{ ...pdfBox(data.additionalProvisions, { flex: true, multiline: true }), minHeight: 60 }}>
    {data.additionalProvisions || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
  </div>

  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      fontSize: 11,
      color: "#6b7280",
      marginTop: 8,
    }}
  >
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      Initialled by Customer
      <span
        className="initial-field adobe-sig"
        style={{
          border: "1px solid #9ca3af",
          display: "inline-block",
          width: 60,
          height: 20,
          textAlign: "center",
          fontSize: 9,
          color: "#6b7280",
        }}
      >
        {!isSupportUser && "{{Int2_es_:signer1:initials}}"}
      </span>
    </span>
  </div>
</div>

          <div
            className="signature-section"
            data-section="rental_page2_footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 11,
              color: "#6b7280",
              marginTop: 24,
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Initialled by Customer:
              <span className="initial-field adobe-sig" style={{ border: "1px solid #9ca3af", display: "inline-block", width: 60, height: 20, textAlign: "center", fontSize: 9, color: "#6b7280" }}>
               {!isSupportUser && "{{Int1_es_:signer1:initials}}"}
              </span>
            </span>
            <span>All areas have been completed and acknowledged prior to initialling this page.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed space-y-4">
      
      
      {renderTable("Equipment", data.equipmentRows, (r) => onChange("equipmentRows", r), "New/Used")}
      <hr className="border-gray-300" />
      <h3 className="text-sm font-bold">Service Provision</h3>
      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 mb-1">
        Non-standard / one-off charges — leave blank unless applicable. These are invoiced by Fibre directly.
      </div>
      {renderTable("Non Standard / One Off Charges", data.oneOffRows, (r) => onChange("oneOffRows", r), "")}
      <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800 mb-1">
        Recurring charges are auto-populated from the Fibre network services (mirrors the Network Services page). Rate £ column is read-only.
      </div>
      {renderTable("Recurring Charges", data.recurringRows, (r) => onChange("recurringRows", r), "Rate £")}
      <hr className="border-gray-300" />
      <div>
        <h3 className="text-sm font-bold mb-2">Additional provisions</h3>
        <textarea className={`${inputCls} w-full h-24 resize-none`} value={data.additionalProvisions}
          onChange={(e) => onChange("additionalProvisions", e.target.value)}
          placeholder="Enter any additional provisions here..." />
      </div>
    </div>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────
 * DROP-IN REPLACEMENT for `TermsAndConditions` in RentalAgreementTab.jsx.
 *
 * WHAT THIS DOES
 * ──────────────
 * - All 28 clauses + Data Protection Notice (previously spread across the
 *   8 hand-split arrays: clausesLeft/Right/2/3/4) are combined into ONE
 *   ordered list, MASTER_CLAUSES, below.
 * - That list is then laid out column-by-column, page-by-page: the left
 *   column fills up to CHARS_PER_COLUMN, overflow continues in the right
 *   column of the same page, and a new page only starts once both columns
 *   are full — so nothing is dropped and nothing needs re-splitting by
 *   hand if the copy changes later.
 * - Every clause reads as a complete, uninterrupted clause. In the
 *   original data several clauses were cut off mid-sentence at an old
 *   column/page boundary (clause 4, clause 9, clause 16) or had a heading
 *   swallowed into the wrong clause (21/22) or a dangling/duplicated
 *   sentence (12, 14). Those are fixed in MASTER_CLAUSES — see the
 *   inline comments marked "FIX:" for exactly what changed and why.
 * - One duplicate has been removed: the full text of clause 11 (Exclusion
 *   and/or Limitation of Liability) was repeated verbatim a second time
 *   inside the Data Protection Notice section in the original data. That
 *   looks like a copy/paste error, so it's been removed here rather than
 *   printed twice — flagged in a comment at that spot too, in case it was
 *   actually intentional and needs restoring.
 *
 * TUNABLES are at the top if you test-render this and want to adjust
 * pagination balance or type size.
 * ─────────────────────────────────────────────────────────────────────────
 */



// ── Tunables ────────────────────────────────────────────────────────────
const COLS_PER_PAGE = 2;
const FONT_BODY = 9.5;    // px
const FONT_TITLE = 10.5;  // px
const FONT_NUM = 10.5;    // px
const LINE_HEIGHT = 1.4;
const CLAUSE_GAP = 9;     // px, space below each clause block

// ── Master clause list (flattened + fixed, in original order) ──────────
const MASTER_CLAUSES = [
  {
    num: "1",
    title: "Definitions and interpretation",
    isFirst: true,
    body: `In these terms and conditions (unless the context otherwise requires):\n\n"Agreement"- means this agreement incorporating the Schedule and these Terms and Conditions"\n\n"Call" means a signal, message or communication which can be silent, visual or spoken on each Line that a Carrier provides at a Site.\n\n"Carrier" means a licensed public switched telecommunications network provider.\n\n"Customer" means a company taking the service on their line, details of which are set out in the Schedule.\n\n"Date of Agreement" means the date set out in the Schedule when the Agreement is signed by Supplier.\n\n"Equipment" means the equipment as set out in the Schedule.\n\n"Supplier" means Fibre Telecoms UK (Fibre) Co. Reg. 11276097\n\n"Supplier or Carrier Equipment" means equipment (including any software) (which for the avoidance of doubt shall exclude the Equipment) which is owned by Supplier or the Carrier and placed on a Site by Supplier or the Carrier or anyone acting on Supplier or the Carrier's behalf for the provision of the Service.\n\n"Funder" means any third party finance company to whom the benefit of this Agreement is assigned;\n\n"Initial Rental Payment" means the initial rental payment specified in the Agreement Terms section of the Schedule;\n\n"Line" means a connection to the Carrier's network.\n\n"Fixed Period" means the period of the term of this Agreement as set out in the Schedule.;\n\n"Normal Working Hours" means: (i) for the implementation of orders by Supplier, 0900-1730 Monday to Friday (excluding UK Public and Bank Holidays) unless otherwise agreed by the parties from time to time; and (ii). for the provision of Service, as the "normal working hours" as set out in the welcome pack provided by Supplier to the Customer.\n\n"Payment" means the sums (including but not limited to the Initial Rental Payment and Period Rental Payments) payable by the Customer as specified in the Agreement Terms section overleaf for the hire of the Equipment.\n\n"Period Rental Payment" means the periodic rentals specified in the Agreement Terms section of the Schedule;\n\n"Service" means the telecommunications services to be supplied by Supplier pursuant to this Agreement as described in the Schedule and excluding the hire of the Equipment;\n\n"Service Charge" means the charges made by Supplier for the provision of the Service (such sum to be certified by Supplier or any Funder as and when relevant) and included in the Service Provision section overleaf;\n\nSchedule" means the schedule forming part of this Agreement which provides details of the Equipment and Service;\n\n"Service Terms" means the terms and conditions relating to the provision of the Service set out in clauses 12 to 19 below.\n\n"Site" means a place at which a Carrier agrees to provide the Service.\n\n"Total Loss" shall have the meaning set out in clause 7.1\n\nWords in the singular shall include the plural and vice versa, references to any gender shall include the other and reference to legal persons shall include natural persons and vice versa.\n\nThe headings in these terms and conditions are intended for reference only and shall not affect their construction.`,
  },
  {
    num: "2",
    title: "The Equipment and the Lease Term",
    body: `2.1  Supplier agrees to let and the Customer agrees to take on lease the Equipment set out overleaf in accordance with this Agreement.The Equipment shall include all replacements, replacement parts, items fitted to the Equipment, logbooks, handbooks, manuals and service records.\n2.2  The Fixed Period of the lease shall be as set out overleaf. The hiring of the Equipment will start on the Date of this Agreement, and will, SUBJECT TO THE Lessor's rights to terminate the hiring under this Agreement, end at the conclusion of the Fixed Period.On expiry or termination of the hiring of the Equipment, the Customer shall be obliged to return the Equipment to Supplier in accordance with clause 8.1.\n 2.3 Provided that the Customer is not in default in the payment of any sum payable under or otherwise in breach of any of the provisions of this Agreement, it shall be entitled to enjoy quiet possession of the Equipment until the hiring ends.`,
  },
  {
    num: "3",
    title: "Payments",
    body: `3.1 All payments and other sums due from the Customer under this Agreement shall be paid by the relevant due date as set out or otherwise indicated in the Schedule.Punctual payment by the Customer of all Payments and other sums due under this Agreement is a vital condition of this Agreement;\n  3.2 Subject to clauses 3.6 and 18.3, all Payments and other sums due under this Agreement from the Customer shall be made without demand by direct debit or any other means agreed by Supplier and shall be made without deduction, set-off, withholding or counterclaim \n 3.3 All Payments and any other sums due under this Agreement which attract VAT shall be subject to VAT at the rate prevailing when the amount is due for payment.\n 3.4 Payments and other sums made by the Customer by post are made at the Customer's risk. If the Customer does not pay the Payments and other sums by direct debit,Supplier may increase the Payments and such other sums by up to 3% to reflect its increased administration costs \n 3.5 The Customer shall pay interest on all overdue sums under this Agreement calculated on a daily basis and compounded quarterly from the due date until payment (both before and after judgement) at the rate of 5% per year over Bank of England Base Rate from time to time. On each occasion that a payment by the Customer is refused or otherwise returned, the Customer shall pay a sum equal to £15 plus VAT to reflect the Customer's associated administration costs.\n 3.6. Without prejudice to the generality of Clauses 3.1 and 3.2 above, the Customer acknowledges that Supplier will assign the benefit of this Agreement to a Funder in order to finance the Equipment and that the Customer must pay all of the Payments(excluding the Service Charge) and any other sums due under this Agreement on the dates they are due without taking off any amounts whatsoever. This is an essential condition of this Agreement and the Funder relies on this condition when agreeing to finance the Equipment. If the Customer does not pay the Payments (less Service Charges) and such other sums on time, this will mean the Customer has repudiated this Agreement and Supplier or the Funder shall have the rights set out in Clause 10. \n 3.7. The Customer must continue to pay the Payments for the benefit of the Funder (excluding Service Charge) even if the Equipment cannot be used for any reason.`,
  },
  {
    // FIX: originally cut off mid-sentence ("...respect of the Equipment. The")
    // at the end of clausesLeft, continued with no number/title at the top
    // of clausesRight. Merged into one clause here.
    num: "4",
    title: "Taxation",
    body: `The Customer will not claim any capital allowances in respect of the Equipment. The Payments have been calculated on the assumption that there will be no change in the nature method or basis of taxation of companies and groups of companies in the United Kingdom including the rate of corporation tax, the availability of capital allowances and the treatment of losses and expenses. If any such change does occur at any time, Supplier or its assignee shall be entitled to adjust the Payments or seek a lump sum from the Customer to ensure that its after tax return is not reduced.`,
  },
  {
    num: "5",
    title: "Inspection, Use and Care of the Goods",
    body: ` The Customer shall: \n\n (a) notify Supplier in writing within 7 days of delivery of the Equipment of any defect in or issue with the Equipment. If the Customer fails to do so it shall be conclusively presumed that the Equipment is complete and in good order and condition and fit for the purpose for which it is required and in every way satisfactory to the Customer. Where the Equipment is satisfactory to the Customer, the Customer shall sign a certificate of acceptance in respect of the Equipment if required by Supplier ; \n\n (b) allow Supplier or its duly authorised agent or representative upon reasonable notice at any time access to inspect the Equipment and any records relating to the Equipment;\n\n(c) keep the Equipment in its control and at the Site Address set out in the Schedule;\n\n(d) use the Equipment in a skilful and proper manner and in accordance with any operating instructions issued for it and any statutory requirements and shall ensure that the Equipment is operated and used by properly skilled and trained personnel;\n (e) keep the Equipment at its own expense and at all times in good repair, condition and working order, properly serviced and maintained and shall keep records of the same;\n(f) not make any alteration to the Equipment or remove any component from the Equipment unless it is replaced immediately (or if removed in the ordinary course of repair and maintenance as soon as practicable) by the same component or by one of a like make and model to that removed or any improved or advanced version of it;\n\n(g) comply with all statutory and other obligations of all kinds in relation to the Equipment and the use of it and at its own expense to add or install with the Equipment any safety or other equipment required by any applicable law or regulation to be added or installed;\n\n(h) not mortgage, charge, pledge, sell, sub-hire or allow any lien to subsist over the Equipment or any of it`,
  },
  {
    num: "6",
    title: "Insurance",
    body: `6.1 Risk in the Equipment shall be borne by the Customer from the date on which the Equipment is delivered to the Customer until the Equipment is returned to or recovered by Supplier\n\n 6.2 At all times during the period referred to in clause 6.1 above, the Customer shall insure the Equipment (in the joint name of the Customer and Supplier ) without restriction or excess against \n\n (a) all risks of loss or damage for it's full replacement value; and\n (b) third party liability for such amount as Supplier may require from time to time or, in the absence of any stipulation, for the amount which is prudent in all the circumstances.\n 6.3 The Customer shall pay punctually all premiums due for the insurances required under clause 6.2 and, at the request of Supplier, produce to Supplier the related policy or policies together with evidence of payment of the premiums. If the Customer fails to do so,Supplier may (but is under no obligation to do so) pay the relevant premium or effect the insurances required and the Customer shall reimburse Supplier on demand the cost of doing so.`,
  },
  {
    num: "7",
    title: "Total Loss and Damage to the Equipment",
    body: `7.1 The Customer shall inform Supplier in writing immediately upon the confiscation of or any loss of or damage to the Equipment. If, in the opinion of Supplier or the insurer, there is a total loss, constructive loss or arranged total loss of the Equipment (each a "Total Loss"),the Customer shall pay to Supplier forthwith an amount equal to the sum calculated under clause 10 as if Supplier had been entitled to terminate and had terminated the hiring of the Equipment under clause 9.1 on the date of the occurrence giving rise to the Total Loss save that in the calculation of the sum due under clause 10, deduction of the net sale proceeds shall be replaced by the deduction of the amount of the insurance money (if any) received by Supplier under the policy or policies maintained pursuant to clause 6.2. \n 7.2 Immediately upon the occurrence giving rise to the Total Loss, the hiring of the Equipment shall terminate but without prejudice to this Agreement which shall continue in full force and effect and to any claims or liabilities then outstanding or arising in the future in connection with this Agreement \n 7.3 If loss or damage occurs that does not amount to a Total Loss, the Customer shall apply any insurance monies paid in respect of the loss or damage in making good the loss`,
  },
  {
    num: "8",
    title: "Return of the Goods",
    body: `8.1 On expiry or earlier termination of this Agreement, the Customer shall, on demand, at its own expense redeliver the Equipment to a location in the United Kingdom notified to the Customer by Supplier in good repair and working order and commensurate with the performance by the Customer of it's obligations under this Agreement`,
  },
  {
    // FIX: originally cut off mid-list at "...affecting the " at the end of
    // clausesRight, continued with no number/title at the top of
    // clausesLeft2 ("Equipment); (j) ..."). Merged into one clause here.
    // Note: (f)/(g)/(h) below are three identical sentences in the
    // original source text (not something introduced by this fix) — left
    // as-is since that's a content question, not a layout artifact.
    num: "9",
    title: "Default or Breach",
    body: `9.1 Without prejudice to Supplier's right to arrears of Payments or other sums due or for damages for breach of this Agreement, Supplier may, upon giving any notice required by law, terminate the hiring under this Agreement on the occurrence of any of the following events (such events specified in (a) to (c) below being deemed repudiatory), if: \n (a) the Customer fails to pay any Payment or other sum due under this Agreement by its due date;\n (b) the Customer fails to comply with or breaches any other provision of this Agreement and, where such breach is capable of remedy, fails to remedy the same within 7 days of a notice from Supplier requiring its remedy; \n (c) the Customer fails to pay any sum due under or breaches the terms of any other agreement in force between it and Supplier or any member of Supplier's group or any loan, lease, debt or security agreement in force between it and any other party;\n(d) the Customer shall be unable to pay its debts as they fall due;\n (e) the Customer shall die, have a bankruptcy order made against him (or in Scotland be sequestrated or become apparently insolvent);\n(f) the Customer shall be liquidated or wound up or have a petition for winding up presented against it or pass a resolution for voluntary winding up (otherwise than in the course of a reconstruction approved by Supplier );\n(g)the Customer shall have presented against it a petition for the appointment of an
    administrator or any steps are taken to appoint an administrator or the Customer has a receiver or administrative receiver appointed over all or any of its assets;\n (h) the Customer shall convene any meeting of all or any of its creditors or make a deed of assignment or arrangement or otherwise compound with all or any of its creditors;\n (i) any step shall be taken to levy distress or execution or any distress or execution is levied or threatened to be levied upon the goods of or in possession of the Customer (or in Scotland any form of diligence is done or threatened by a third party affecting the Equipment);\n (j) there shall, in the opinion of Supplier, be a material adverse change in the financial position or business of the Customer; or\n (k) there is any change in the control, ownership or shareholding of the Customer or any holding company of the Customer from that existing at the date of this Agreement;\n(l)any licence under which the Customer has the right to run it's telecommunication system and connect it to Supplier's system is revoked, amended or otherwise ceases to be valid\n(m)there is any failure to comply with clause 28 of this Agreement and/or the Customer, the Guarantor and/or (if the Customer is a company or partnership) any member of its Group is or becomes a Restricted Party`,
  },
  {
    num: "10",
    title: "Consequences of Termination",
    body: `Immediately upon termination of the hiring of the Equipment, the Customer shall pay to Supplier the following sums:\n (a) all arrears of Payments and other sums due under this Agreement; plus\n(b) a sum equal to all the Payments that would have been payable (in the absence of any early termination) during the remainder of the Fixed Period (less a discount for early payment on each such Payment at the rate of 3% a year from the date of termination to the date the Payment would have been due) plus; \n(c) a sum equal to the Service Charges that would have  been payable (in the absence of any early termination) during the remainder of the Fixed Period (at a discount of 50% a year from the date of termination to the date the Service Charges would have been due) plus \n (d)damages for any breach of this Agreement and all expenses and costs incurred by Supplier in retaking possession of and selling or attempting to sell the Equipment and enforcing its rights under this Agreement; and less \n (e)the net proceeds of sale of the Equipment (which shall be an amount equal to the proceeds of sale of the Equipment received by Supplier (excluding any VAT) less Supplier's expenses in connection with the recovery, possession, insurance, storing and sale of the Equipment`,
  },
  {
    num: "11",
    title: "Exclusion and/or Limitation of Liability",
    body: `11.1.1 The Equipment is supplied to the Customer subject to Service Terms. The extent of Supplier's liability in respect of the Equipment and the extent of any guarantees, terms,warranties or representations made or given by it in respect of the Equipment are set out in this Clause 11 and the Service Terms. Supplier may assign the benefit of this Agreement to a Funder for the purposes of financing the Equipment and the Customer therefore agrees that any enforcement of the Customer's rights relating to such guarantees, terms,warranties or representations as specified in this Clause 11 and the Service Terms must be by way of direct personal action against Supplier and may not be by way of set- off, claim, withholding or counterclaim in relation to any rights Supplier has so assigned.\n 11.1.2 If Supplier assigns this Agreement to a Funder the Customer agrees in order for a Funder to facilitate the funding of the transaction that the Customer's obligation to pay the Payments, excluding Service Charges, shall not be subject to any right of set-off, counter-claim, defence, abatement, suspension, deferment or reduction as against Supplier which the Customer now or later has with regard to any claim against Supplier in respect of or arising out of this Agreement. This does not prevent the Customer claiming against Supplier, by way of direct personal action, under the terms of this Agreement.\n 11.1.3 The Customer is responsible for choosing the Equipment and for making sure that it is fit and suitable for the needs of the Customer.\n 11.1.4 Supplier or the Funder will not be responsible for any liability, claim, loss or damage caused directly or indirectly by the Equipment and in no event will the Funder's liability to the Customer exceed the aggregate of the Payments (excluding Service Charges) paid by the Customer at the time the liability arises.\n 11.1.5 If the Customer does not obtain from the manufacturer express warranties about the Equipment Supplier will endeavour at the Customer's request and cost to transfer to the Customer the benefits of any manufacturer's express warranties given to Supplier in respect of the Equipment\n 11.1.6 Supplier does not restrict or exclude it's liability in respect of death or personal injury to the extent that it results from it's negligence or that of its employees in the course of their employment by it.\n 11.1.7 Supplier shall not be liable to the Customer for any loss or damage which may be suffered by it due to any cause beyond Supplier's reasonable control including without limitation any act of God, inclement weather, failure or shortage of power supplies, flood, drought, lightning or fire, strike, lock-out, trade dispute or labour disturbance, any act or omission of Government, highways authorities, other public telecommunication operators or other competent authority, or production or supply of service by third parties.\n 11.1.8 Supplier shall not be liable for any claims arising from the Customer or end users being subject to fraudulent use of their service or from a third party succeeding in "hacking" into the Customer's Equipment, all costs or claims arising therefrom will be paid in full by the Customer. Notwithstanding the foregoing, Supplier will endeavour to notify the Customer using the most expedient means available of any fraudulent activity however Supplier can offer no guarantee or contractual obligation in relation to the detection of fraud. Supplier is not liable in contract, tort (including negligence) or otherwise for indirect \n 11.1.9 loss of profits, business or anticipated savings, nor for any other indirect loss or damage or for any destruction of data.\n11.1.10 Each provision of this Agreement, excluding or limiting liability, operates separately. If any part is held by a court to be unreasonable or inapplicable, the other parts shall continue to apply.\n 11.1.11 Nothing in these terms and conditions shall impose any liability upon Supplier in respect of any non-performance of Services which are not performed in accordance with this Agreement arising out of the Customer's own acts, omissions, negligence or default`,
  },
  {
    num: "",
    title: "Software",
    body: `11.2 If the Equipment includes or consists of software the Customer further agrees and acknowledges that:\n 11.2.1 although the purchase price paid by Supplier for the Equipment includes the right to use the software Supplier does not acquire any legal title in it; the Customer must obtain any licence required to use the software directly from the software owner;\n11.2.3 it is a condition of this Agreement that the Customer will comply with all of the terms of any licence or sub-licence for the use of the software; maintenance of the software is not included in this Agreement and no defect in the \n 11.2.4 software or any event which prevents the Customer using the software will affect the Customer's obligation to make Payments as provided under this Agreement; the Customer will obtain any warranties or guarantees it requires for the software\n 11.2.5 including its fitness for purpose directly from the supplier of the software and Supplier gives no express warranties and hereby excludes all warranties, conditions or guarantees that may otherwise be implied in relation to any software`,
  },
  {
    num: "",
    title: "Service Terms",
    body: ``,
  },
  {
    num: "12",
    title: "Provision of the Service",
    // FIX: original body ended on the dangling word "...available on request from"
    // with no continuation anywhere in the source. Closed the sentence.
    body: `12.1 Supplier will use reasonable endeavours to provide the Service by the dates agreed with the Customer but all dates are estimates and Supplier has no liability for any failure to meet those dates.\n 12.2 Subject always to paragraph 12.1 above, Supplier will use reasonable efforts to provide uninterrupted Service to the Customer. From time to time faults may occur which Supplier will liaise with the Carrier to repair in accordance with the fault repair service.\n 12.3 If appropriate, the Customer authorises Supplier, it's agents, employees or other authorised personnel, to reprogram and/or install access equipment, in order to provide the Service. Activation of broadband Service. Before being able to activate the Service, Supplier will:\n 12.4\n (a)verify that the Customer's premises are in an area in which the Service is available; and carry out a line test; \n (b) if Supplier is unable to activate the Service due to the Customer's act or omission or due to incorrect information being provided by the Customer, Supplier reserves the right to charge the Customer a failed connection fee of £30; \n (c) occasionally visits to the Customer premises will be required. These visits are sometimes carried out by a Carrier. Supplier will liaise with the Customer to arrange the timing of these appointments. It is also possible that a Carrier may contact the Customer directly in relation to the appointment;\n (d)during activation of the Service, the Customer may temporarily lose the use of its other telecommunications services;\n(e) if the Customer requires a static IP address it will need to specify this when ordering from Supplier. If the Customer does not do not specify this requirement it will receive either a dynamic or a static IP address at Supplier's discretion.\n 12.5 As part of the Service, Supplier will provide a monthly allowance of 4,000 minutes of Local,National (01,02) and UK mobile calls per user, per month. Any usage outside of this allowance (including but not limited to non-geographic and international calls) will be charged as per Supplier's standard tariff of call charges, available on request.`,
  },
  {
    // FIX: original title was garbled ("Supplier .Supplier , a Carrier or BT
    // Equipment or Equipment hired by Supplier") — cleaned up.
    num: "13",
    title: "Supplier , a Carrier or BT Equipment or Equipment hired by Supplier",
    body: `13.1 The Supplier or Carrier Equipment remains the property of Supplier (or the Funder, if applicable) or the Carrier at all times.\n 13.2 If Supplier or the Carrier needs to install Supplier or Carrier Equipment at a Site to enable Supplier, the Carrier or BT to provide the Service, the Customer will use reasonable endeavours to procure that prior to installation:\n (a) it has prepared the Site in accordance with Supplier or the Carrier's reasonable instructions, if any; \n (b) it will make available a suitable place and conditions for the Supplier or Carrier or Equipment;\n (c)it will provide at no charge to Supplier or the Carrier sufficient electricity to power the Supplier or Carrier Equipment; and \n (d) it agrees to restore the condition of the Site including any re-decorating that may be required after installation is completed.\n13.3 The Customer is responsible for the Supplier or Carrier Equipment and must not add to, modify, carry out any maintenance on or in any way interfere with the Equipment nor allow anyone else, (other than someone authorised by Supplier) to do so. The Customer will be liable to Supplier for any loss of or damage to the Equipment, except where such loss or damage is due to fair wear and tear or is caused by Supplier or the Carrier, or anyone acting on Supplier's behalf.\n13.4 For any Supplier or Carrier Equipment, Supplier does not guarantee the continuing availability of any such equipment and Supplier reserves the right to add to, substitute or to discontinue equipment.`,
  },
  {
    // FIX: original body repeated its own opening sentence twice before
    // "14.1"; the duplicate lead-in has been removed.
    num: "14",
    title: "Connection of Equipment to the Service",
    body: `14.1 Any Equipment connected to or used with the Service must be connected and used in accordance with any published instructions and any safety or security procedures applicable to the use of that Equipment\n 14.2 Any Customer equipment which is attached (directly or indirectly) to the Service must be approved or compliant with any relevant legislation.`,
  },
  {
    num: "15",
    title: "Access and Site Regulations",
    body: `15.1 To enable Supplier to carry out its obligations under this Agreement, the Customer will use its reasonable endeavours to make arrangements with the Site to provide Supplier or the Carrier and its employees, and anyone acting on their behalf, who produces a valid identity card, with access to any Site and any other premises outside Supplier or the Carrier's control, at all reasonable times. Supplier and the Carrier will normally only require access during Normal Working Hours but may, on reasonable notice, require the Customer to provide or make arrangements with the End User to provide access at other times. Any work carried out of hours must be agreed in advance and is subject to an additional charge.\n 15.2 Supplier employees and anyone acting an Supplier's behalf will observe the Customer's reasonable Site regulations. In the event of any conflict between the Site regulations and these terms and conditions, these terms and conditions will prevail.\n 15.3 The Customer will provide a suitable and safe working environment for Supplier employees and anyone acting on Supplier's behalf.\n 15.4 If the Customer requests maintenance or repair work which is found to be unnecessary, the Customer may be charged for the work and the costs incurred. Supplier will give notice that work is considered unnecessary prior to completion or raising charges therefore.`,
  },
  {
    // FIX: originally cut off mid-list at "...corrupt the Services and/or
    // Equipment in any way; or" at the end of clausesRight2, continued with
    // no number/title at the top of clausesLeft3 ("(f) in a way which is..."
    // through "...communicated in advance by Supplier to the Customer in
    // writing."). Merged into one clause here.
    num: "16",
    title: "Use of the Service",
    body: `16.1 The Customer must not use the Service or knowingly allow or permit any member of staff to use the Supplier Service:\n (a)  in a way that does not comply with the terms of any agreement (as appropriate) or that is in any way unlawful or fraudulent or has any unlawful or fraudulent purpose or effect; or \n (b) for the transmission of any material which is defamatory, offensive, abusive, or of an obscene or menacing character; or\n (c)  in a manner which constitutes a violation or infringement of the rights of any person, firm or company (including but not limited to rights of copyright or confidentiality); or\n(d) in breach of any statutory provision, regulation or code of practice from time to time in effect relating to transmission of telecommunication services; or\n (e) in a way which allows third parties to interfere with or corrupt the Services and/or Equipment in any way; or\n(f) in a way which is, or is intended to be, in any way unlawful or fraudulent; or \n (g) in a way that in Supplier's reasonable opinion could materially affect the quality of any electronic communications service or other service provided by Supplier or any third party, or any network used to provide such service; or\n (h) in any manner which shall contravene the reasonable requirements which have been communicated in advance by Supplier to the Customer in writing.`,
  },
  {
    num: "17",
    title: "Allocation and Use of Telephone Numbers",
    body: `17.1 In the event that Supplier allocates any telephone numbers to the Customer for the purpose of providing the Services the Customer acknowledges that it shall not acquire any legal, equitable or proprietorial right to any such numbers and Supplier shall be entitled to withdraw or change any telephone number or code or group of numbers or codes upon giving the Customer reasonable written notice.\n 17.2 For the avoidance or doubt, any and all intellectual property rights in any such telephone number shall at all times, as between Supplier and the Customer, remain vested in Supplier.\n 17.3 When a number ports to Supplier, any directory entries associated with that number are also ported over to Supplier on BT's Directory Management System (DMS). You should check with the current provider for any chargeable directory entries before porting to Supplier and cease them if not required, else charges will be incurred. The previous provider may not have charged for this service, but Supplier will get charged by BT and this charge is passed on accordingly, therefore it is important to check for entries even if no charges are currently being incurred.`,
  },
  {
    // FIX: original had num "18" with an empty title, and the heading text
    // ("Suspension of Service") was stranded inside the body ahead of
    // "18.1". Moved the heading to the title field where it belongs.
    num: "18",
    title: "Suspension of Service",
    body: `18.1 Supplier may at it's sole discretion upon giving the Customer written notice elect to suspend forthwith provision of the Services until further notice without compensation on notifying the Customer either orally (confirming such notification in writing) or in writing in the event that:\n (a) Supplier is entitled to terminate this Agreement; or\n(b)Supplier is obliged to comply with an order, instruction or request of the UK Government, an emergency services organisation, the provision of telecommunications services or the establishment of networks or any information provided across them or other competent administrative authority;\n (c) Supplier needs to carry out any emergency works to the network or any Equipment installed at the Customer's premises by Supplier for the purpose of providing the Services.\n18.2  Where any suspension of the Services is implemented as a consequence of the Customer's breach, fault or omission (but not otherwise), the Customer shall reimburse Supplier for all costs and expenses incurred by it's implementation of such suspension and/or the recommencement of the provision of the Services as appropriate.\n 18.3 If Supplier exercises its right to suspend the Services this shall not restrict its right to terminate the Agreement nor shall it affect the Customer's obligation to make Payments and/or pay the sum due upon termination of this Agreement under Clause 10 to any Funder.`,
  },
  {
    num: "19",
    title: "Service Charges",
    body: `19.1 Supplier shall be entitled to increase its charges at any time, such increase to apply to all Services provided after the date of the increase and to be reflected in the next Service Charge. If the increase is greater than 15% Supplier shall give the Customer 30 days' notice of any such increase, such increase shall take effect and be reflected on the next Service Charge following the notice.\n 19.2 We shall prepare and send invoices for usage charges each calendar month in arrears or in such other form and manner as shall be agreed with you. Usage charges payable shall be calculated by reference to data recorded or logged by us and not be reference to any data recorded or logged by you and such data shall, in the absence of manifest error be final and binding.`,
  },
  {
    num: "20",
    title: "Representations",
    body: `No statement, description, information, warranty, condition or recommendation contained in any catalogue, price list, advertisement or communication or made verbally by any of our agents or employees shall be construed to enlarge, vary or override in any way any of these terms and conditions`,
  },
  {
    // FIX: the heading "Assignment and Sub-Contracting" was originally
    // stuck at the end of THIS clause's body (Confidentiality), even
    // though it's actually the heading for clause 22 below. Moved out to
    // its own heading entry, immediately after this clause.
    num: "21",
    title: "Confidentiality",
    body: `Save to a Funder, neither Supplier nor the Customer shall whilst this Agreement is in force or thereafter disclose any of the other's confidential information nor any details of the other's commercial or technical activities or policy except insofar as is strictly necessary for fulfilling its obligations hereunder and except for any disclosure required by statute or law and save for information which is or subsequently enters the public domain.`,
  },
  {
    num: "22",
    title: "",
     body: `22.1 Supplier may assign this Agreement to a Funder or sub contract the whole or any part of the performance of the Services to any person, firm or company without the Customer's prior written consent.\n 22.2 The Customer shall not assign or delegate or otherwise deal with all or any of its rights or obligations under this Agreement without Supplier's prior written consent or the written consent of any Funder notified to the Customer.`
  },
 
  {
    num: "23",
    title: "Step in Rights",
    body: `23.1 If Supplier shall fail to provide the Service in accordance with these terms and conditions (a "Service Default") the Customer shall notify Supplier in writing together with a copy to any Funder whose identity has been notified to the Customer.\n23.2 If a Service Default exists and is not remedied within 30 days of receipt of the Customer's notice then the Customer may not terminate this Agreement and may, as its sole remedy, request Supplier or any Funder to appoint an alternative contractor to perform Supplier's obligations under this Agreement. The alternative contractor so appointed may, in conjunction with the Funder, require certain amendments to this Agreement solely to reflect its appointments to supply the Services in place of Supplier.`,
  },
  {
    num: "24",
    title: "Entire Agreement",
    body: `24.1 This Agreement contains the whole agreement between the parties and supersedes all previous written or oral agreements relating to its subject matter.\n 24.2 The parties acknowledge and agree that:\n(a)the parties have not been induced to enter into this Agreement by any representation, warranty or other assurance not expressly incorporated into it;\n(b)in connection with this Agreement the party's only rights and remedies in relation to any representation, warranty or other assurance are for breach of this Agreement and that all other rights and remedies are excluded\n 24.3 The provisions of paragraphs 24.1 and 24.2 shall not affect the parties' rights or remedies in relation to any fraud or fraudulent misrepresentation.\n 24.4 Save for any Funder, a person who is not party to this Contract has no right under the Contracts (Rights of Third Parties) Act 1999 to enforce any term of this Agreement, but this does not affect any right or remedy of a third party which exists or is available apart from that Act.`,
  },
  {
    num: "25",
    title: "Severability",
    body: `If any provision of this Agreement is held invalid, illegal or unenforceable for any reason by any court of competent jurisdiction, such provision shall be severed and the remainder of the provisions of this Agreement will continue in full force and effect as if this Agreement had been executed with the invalid, illegal or unenforceable provision omitted.`,
  },
  {
    num: "26",
    title: "Indemnity",
    body: `26.1 The Customer shall indemnify Supplier and/or any Funder and keep Supplier and/or any Funder indemnified on demand against all losses, charges, damages, legal expenses (on a full indemnity basis), proceedings, judgements and liabilities directly or indirectly incurred by Supplier and/or any Funder:\n(a) by reason of any failure or alleged failure by the Customer to comply with any of its obligations under this Agreement;\n(b)by reason of any loss, injury or damage suffered by any person in connection with the design, manufacture, testing, delivery, possession, hiring, transportation, maintenance, ownership, condition, insurance, use, operation, removal, return, sale or disposal of the Equipment or any defect of the Equipment;\n (c)in respect of all loss or damage to the Equipment (insofar as Supplier or the Funder (as appropriate) shall not be reimbursed for the same out of any proceeds of insurance in respect of the Equipment) occurring at any time before the Equipment is redelivered to or recovered by Supplier or the Funder regardless of the cause of the loss or damage; \n(d)and in respect of the affixation or removal of the Equipment to or from the land or buildings at which it is located`,
  },
  {
    num: "27",
    title: "General",
    body: `27.1 Where the Customer comprises more than one person, each person shall be jointly and severally liable under this Agreement.\n 27.2 The Customer shall provide Supplier with management accounts, financial statements and other information in relation to the Customer's business and financial position, the use of the Equipment and the location of the Equipment as Supplier may reasonably require from time to time.\n 27.3 These terms and conditions shall apply to this Agreement to the exclusion of any other terms and conditions contained or referred to in any order, letter, form of contract or other communication sent by the Customer to Supplier and the provisions to these terms and conditions shall prevail, unless expressly varied in writing and signed by a director on Supplier's behalf.\n27.4 Any delay or failure of Supplier to exercise any right or remedy shall not constitute a waiver of it or them.\n27.5 This Agreement shall be governed by and construed in accordance with the law of England and Wales and the parties submit to the exclusive jurisdiction of the courts of England and Wales\n27.6 Electronic Signature: This Agreement may be signed by you and by us by way of electronic signature, as defined in section 7(2) of the Electronic Communications Act 2000.`,
  },
  {
    num: "28",
    title: "Sanctions",
    body: `28.1 You shall not (and (if You are a company or partnership) shall ensure that no other member of your Group shall) conduct your or their business in material violation of any applicable Anti-Bribery and Corruption Laws.\n 28.2 You shall (and shall ensure that each other member of your Group shall) maintain systems, controls, policies and procedures designed to seek to prevent violation of any applicable Anti-Bribery and Corruption Laws.\n28.3 You shall not fund any payment due to Supplier out of proceeds derived from transactions that violate the prohibitions set forth in any Sanctions.\n 28.4 You shall not, and shall ensure that no Guarantor or other member of your Group shall, become a Restricted Party.\n28.5 You shall ensure that none of the funds or assets used to repay Supplier shall constitute property of, or shall be beneficially owned directly or indirectly by, any Restricted Party.`
  },
  {
    num: "",
    title: "",
    body: `28.6 You shall ensure that none of the proceeds of any funds transferred to You by Supplier are used in violation of Anti-Bribery and Corruption Laws and/or Sanctions.\n28.7 Supplier does not have to accept any repayment or other sum payable under this agreement\n (a) if it does not think it came from You,\n (b)  it suspects that it is connected to fraud or any other criminal activity, including where it think the funds involved may have been obtained through deception;.\nit thinks that accepting it might break a law, regulation, code or other duty that applies to Supplier, or it might expose it to claims from third parties; or\n(d)it believes that accepting it might expose Supplier (or any member of your group of companies) to legal action or censure from any government, regulator or law enforcement agency\n 28.8 Unless the law prevents Supplier, Supplier will notify You if it is unable to accept a payment and explain why. If Supplier declines to accept a payment then You will remain liable to pay the relevant amountv to Supplier in accordance with this agreement.\nFor the purposes of this clause, the following terms shall have the meaning given to them below: Anti-Bribery and Corruption Laws means the U.S. Foreign Corrupt Practices Act of 1977 (as amended, and the rules and regulations thereunder), the UK Bribery Act of 2010 or any similar laws, rules or regulations issued, administered or enforced by the United States, United Kingdom, the European Union or any of your member states, or any other country  
or Governmental Agency having jurisdiction over You or any member of your Group.Guarantor means, if applicable, any guarantor that has guaranteed the obligations of You under this Agreement to Supplier.Group means You and each of your Subsidiaries and Holding Companies from time to time and “member of your Group” will be construed accordingly.Holding Company shall bear the meaning given to that expression in section 1159 of the Companies Act 2006 or your equivalent in any jurisdiction.\n OFAC means The Office of Foreign Assets Control of the U.S. Department of the Treasury.\nRestricted Party means a person that is:\n(a) listed on any Sanctions List, or owned by, controlled by, or acting on behalf of a person listed on any Sanctions List; \n(b)ocated in, incorporated under the laws of, or organised under the laws of a country or territory that is the target of country-wide or territory-wide Sanctions; or \n (c) otherwise a target of Sanctions (“target of Sanctions” signifying a person with whom a US person or other national of a Sanctions Authority would be prohibited or restricted by law from engaging in trade, business or other activities).\n Sanctions means any trade, economic, sectoral or financial sanctions laws, regulations,embargoes or restrictive measures administered, enacted or enforced by: (i) the United States government; (ii) the United Nations Security Council; (iii) the European Union;(iv) the United Kingdom; (v) South Africa; or (vi) the respective governmental institutions and agencies of any of the foregoing, including, without limitation, OFAC, the United States Department of State and Her Majesty’s Treasury (HMT) (together the Sanctions Authorities)\n Sanctions List means the “Specially Designated Nationals and Blocked Persons” list maintained by OFAC, the Consolidated List of Financial Sanctions Targets and the Investment Ban List maintained by HMT, or any similar list maintained by, or public announcement of Sanctions\n Subsidiary means a subsidiary within the meaning of section 1159 of the Companies Act 2006 or your equivalent in any jurisdiction 
`,
  },
  {
    num: "",
    title: "Data Protection Notice",
    body: `Supplier ("we", "us", "our") is committed to processing your personal data in accordance with United Kingdom (UK) data protection laws. This Data Protection Notice aims to give you information on how your personal data (i.e., information which directly or indirectly identifies you) are processed by us. For the purposes of UK data protection laws, we are a data controller.\n This notice is supplemental to the data protection processing notice provided before your proposal for finance was submitted. Some aspects of the processing set out in this notice were covered by that earlier notice, but others are only relevant now that your application for finance has been approved in principle and we intend to enter into an agreement with you. Further details of how we will manage your data can be found at http://www.[SUPPLIER].co.uk/privacy-policy`,
  },
  {
    num: "",
    title: "Data That May Be Collected",
    body: `We may collect certain personal data with respect to you as a customer, including, without limitation your name, address, date of birth, contact details, credit reference data, financial and employment details, banking and credit card details, and details of your business. We collect some of this data from third parties, for example credit reference agencies.\n Where a corporate entity is entering into this contract we will collect personal data about the individuals who are directors and shareholders of the business from credit reference agencies where this data is held publicly, such as at Companies House. This notice also applies to the processing of such personal data and use of the word 'you' in this notice will encompass such individuals.\nWe may process sensitive personal data (including, for example, information revealing an individual's physical or mental health). Where sensitive personal data are processed we will obtain your explicit consent for the processing.`,
  },
  // NOTE — REMOVED DUPLICATE: the original data repeated the ENTIRE text of
  // clause 11 ("Exclusion and/or Limitation of Liability") a second time
  // here, inside the Data Protection Notice section, word for word. That
  // reads as a copy/paste error rather than intentional content, so it has
  // been left out to avoid printing the same clause twice. If this was
  // actually meant to appear again here, let me know and I'll restore it.
  {
    num: "",
    title: "Use of Personal Data",
    body: `We will use your personal data for: provision of products and services, credit and AML risk assessment, assessing ongoing credit performance, recoveries, collections, insurance administration, profiling for marketing purposes, market research and product development, statistical analysis, marketing, fraud prevention and detection and otherwise as necessary to comply with applicable laws, regulations and/or codes of practice. The processing of personal data may be necessary for the performance of a contractual relationship, compliance with a legal obligation, or where it is in our legitimate interests, or those of a company within any group of companies of which we are a member.`,
  },
  {
    num: "",
    title: "Disclosure to Certain Third Parties",
    body: `We may disclose certain personal data: (i) within any group of companies of which we are a member and to other affiliates in that group; (ii) to our brokers and dealers / suppliers, professional advisors and service providers (including, information technology systems providers and recovery agents); (iii) to courts, governmental and non-governmental regulators and ombudsmen; (iv) to fraud prevention agencies and law enforcement agencies; (v) to any third party that acquires, or is interested in acquiring, all or part of our assets or shares, or that succeeds us in carrying on all or a part of our business, whether by merger, acquisition, reorganization or otherwise; and (vi) as otherwise required or permitted by law.\n We may also pass your Personal Data on to other companies within any group of companies of which we are a member and/or any relevant third party and both we and/or they may use it for any purpose linked to any sale of and/or granting of security over the agreement we have with you. In such circumstances, such third parties may also use and/or disclose your Personal Data to any third party that they ask to assist them with the preparation for and/or completion of any such sale and/or granting of security; they may also, once such sale and/or granting of security is completed, use and/or disclose your Personal Data to third parties for any of the other purposes which we have outlined in this notice in the same way as if they had entered into the agreement with you instead of us.\n The personal information we have collected will be shared with fraud prevention agencies who will use it to prevent fraud and money-laundering and to verify your identity. If fraud is detected you could be refused certain services, finance or employment. Further details of how your information will be used by us and these fraud prevention agencies, and your data protection rights can be found on our website at http://www.[SUPPLIER].co.uk/privacy-policy. In order to process your application, we and the third parties mentioned above will perform credit and identity checks on you with one or more credit reference agencies ("CRAs"). To do this, we will supply your personal information to CRAs and they will give us information about you, even if your application does not proceed or is unsuccessful. This will include information from your credit application and about your financial situation and financial history. CRAs will supply to us both public (including the electoral register) and shared credit, financial situation and financial history information and fraud prevention information.\nWe will use this information to: (v) Assess your creditworthiness; (vi) Verify the accuracy of the data you have provided to us; (vii) Prevent criminal activity, fraud and money laundering; (viii) Manage your account(s); (ix) Trace and recover debts; and (x) Ensure any offers provided to you are appropriate to your circumstances.\nWe will continue to exchange information about you with CRAs while you have a relationship with us. We will also inform the CRAs about your settled accounts. If you borrow and do not repay in full and on time, CRAs will record the outstanding debt. This information may be supplied to other organisations by CRAs. When CRAs receive a search from us they will place a search footprint on your credit file that may be seen by other lenders.`,
  },
  {
    num: "",
    title: "",
    body: `If you are making a joint application, or tell us that you have a spouse or financial associate, we will link your records together, so you should make sure you discuss this with them, and share with them this information, before lodging the application. CRAs will also link your records together and these links will remain on your and their files until such time as you or your partner successfully files for a disassociation with the CRAs to break that link. The identities of the CRAs, their role also as fraud prevention agencies, the data they hold, the ways in which they use and share personal information, data retention periods and your data protection rights with the CRAs are explained in more detail at each of the three CRAs websites — any of these three links will take you to the same CRAIN document: Callcredit www.callcredit.co.uk/crain; Equifax www.equifax.co.uk/crain; Experian www.experian.co.uk/crain.\nTransfer of Personal Data Outside the European Economic Area ("EEA"). We may transfer your personal data to recipients located in countries outside of the EEA which may not have data privacy laws equivalent to those in the EEA. In those instances, we will take all necessary steps to protect your personal data in accordance with applicable data privacy laws.`,
  },
  {
    num: "",
    title: "Your Rights",
    body: `Under applicable UK data privacy laws, you may have a right to: (i) request access to and rectification or erasure of your personal data; (ii) obtain restriction of processing or to object to processing of your personal data; and (iii) data portability (i.e. to request the transfer of personal data from one data controller to another in certain circumstances). If you wish to exercise any of these rights you should contact the Data Privacy Officer as described below. You also have the right to lodge a complaint about the processing of your personal data with your local data protection authority.`,
  },
  {
    num: "",
    title: "",
    body: `We rely on automated credit assessment based on the personal data you provide to us and data which we obtain from our credit reference agency or similar sources about your credit profile or history. The outcome of this process can result in an automated decline of your application where it does not meet our acceptance criteria. We review this acceptance criteria regularly to ensure fairness in the decisions made. You have a right to ask us to manually review any decision taken in this manner.\n In addition we may use automated profiling to identify whether other products or services may be of use to you when we consider marketing campaigns. You have a right to object to this automated profiling. This is separate to the right you have to object to receive marketing.`,
  },
  {
    num: "",
    title: "Security",
    body: `We will take steps to protect your personal data against loss or theft, as well as from unauthorized access, disclosure, copying, use or modification, regardless of the format in which it is held.`,
  },
  {
    num: "",
    title: "Retention",
    body: `We will normally retain your personal data for a period of three months from the end of your contract. Under certain circumstances this period may vary if we consider it necessary for good operational or commercial reasons, or in order to meet other legal obligations.`,
  },
  {
    num: "",
    title: "Enquiries, Requests or Concerns",
    body: `All enquiries, requests or concerns regarding this Notice or relating to the processing of Personal Data, should be sent to the Data Privacy Officer using the following contact details: billing@fibretelecomsuk.co.uk`,
  },
];

// ── Fill columns/pages by REAL measured height, not estimated length ───
// Character counts are a rough proxy for rendered height at best — they
// don't account for word-wrap, how many short list-item lines a clause
// has ("(a)... (b)... (c)..."), punctuation density, etc. That's exactly
// why a fixed CHARS_PER_COLUMN kept mis-predicting where a column would
// actually run out of room.
//
// This version measures the ACTUAL rendered pixel height of every clause
// in the browser (off-screen, at the same column width used in the real
// layout), then fills columns/pages based on those real measurements.
// A column only moves to the next column/page once its measured content
// would truly overflow — so it can't overflow, because it's built from
// what actually got drawn, not an estimate.
const PAGE_WIDTH_PX = 794; // matches the A4-at-96dpi convention used elsewhere in this file
const PAGE_PADDING_PX = 32;        // left padding, and baseline right padding
const PAGE_PADDING_RIGHT_EXTRA_PX = 110; // EXTRA right-side buffer, on top of PAGE_PADDING_PX — bump this up/down until content clears whatever is covering the right edge on your screen
const COLUMN_GAP_PX = 24;
const COLUMN_SAFETY_MARGIN_PX = 12; // extra slack subtracted per column so wrapping kicks in a bit early rather than right at the edge
const COLUMN_WIDTH_PX = (PAGE_WIDTH_PX - PAGE_PADDING_PX - (PAGE_PADDING_PX + PAGE_PADDING_RIGHT_EXTRA_PX) - COLUMN_GAP_PX) / COLS_PER_PAGE - COLUMN_SAFETY_MARGIN_PX;
// Usable vertical space per column before the page's own header/footer
// eat into it (title block + top/bottom hr + footer row + margins).
const MAX_COLUMN_HEIGHT_PX = 1000;

const layoutIntoPagesByHeight = (itemsWithHeight, colsPerPage, maxColumnHeight) => {
  const pages = [];
  let colItems = Array.from({ length: colsPerPage }, () => []);
  let colHeight = Array(colsPerPage).fill(0);
  let colIdx = 0;

  itemsWithHeight.forEach((item) => {
    const h = item.__measuredHeight || 0;
    if (colItems[colIdx].length > 0 && colHeight[colIdx] + h > maxColumnHeight) {
      colIdx += 1;
      if (colIdx >= colsPerPage) {
        pages.push(colItems);
        colItems = Array.from({ length: colsPerPage }, () => []);
        colHeight = Array(colsPerPage).fill(0);
        colIdx = 0;
      }
    }
    colItems[colIdx].push(item);
    colHeight[colIdx] += h;
  });
  pages.push(colItems);
  return pages;
};

const ClauseBlock = ({ num, title, isFirst, body }) => {
  const numStyle = {
    fontWeight: 700,
    fontSize: FONT_NUM,
    marginBottom: 1,
    fontFamily: "Arial, Helvetica, sans-serif",
  };
  const titleStyle = {
    fontWeight: 700,
    fontSize: FONT_TITLE,
    marginBottom: 1,
    marginLeft: 10,
    fontFamily: "Arial, Helvetica, sans-serif",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const bodyStyle = {
    fontSize: FONT_BODY,
    lineHeight: LINE_HEIGHT,
    marginLeft: 10,
    marginTop: 0,
    whiteSpace: "pre-wrap",
    color: "#222",
    fontFamily: "Arial, Helvetica, sans-serif",
    textAlign: "justify",
    breakInside: "avoid",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const cleanBody = (body || "").replace(/\n{2,}/g, "\n").trim();
  if (!title && !cleanBody) return null;
  return (
    <div style={{ marginBottom: CLAUSE_GAP, breakInside: "avoid" }}>
      {num && <p style={numStyle}>{num}.</p>}
      {title && <p style={titleStyle}>{isFirst ? `1.1\u00a0\u00a0${title}` : title}</p>}
      {cleanBody && <p style={bodyStyle}>{cleanBody}</p>}
    </div>
  );
};

const pageStyle = (isPdfMode) => ({
  background: "#ffffff",
  width: isPdfMode ? PAGE_WIDTH_PX : "100%",
  maxWidth: isPdfMode ? PAGE_WIDTH_PX : "100%",
  marginLeft: isPdfMode ? "auto" : 0,
  marginRight: isPdfMode ? "auto" : 0,
  paddingTop: 20,
  paddingRight: PAGE_PADDING_PX + PAGE_PADDING_RIGHT_EXTRA_PX,
  paddingBottom: 16,
  paddingLeft: PAGE_PADDING_PX,
  fontFamily: "Arial, Helvetica, sans-serif",
  boxSizing: "border-box",
});

const PageBlock = ({ left, right, pageIndex, isPdfMode,isSupportUser }) => (
  <div style={pageStyle(isPdfMode)}>
    {pageIndex === 1 && (
      <>
        <p
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: 16,
            marginBottom: 10,
            lineHeight: 1.3,
            fontFamily: "Arial, Helvetica, sans-serif",
          }}
        >
          Terms and Conditions of Fibre Telecoms UK Limited Hosted Telephony Agreement
        </p>
        <hr style={{ border: "none", borderTop: "1px solid #000", margin: "4px 0" }} />
      </>
    )}

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `0 ${COLUMN_GAP_PX}px`, marginTop: pageIndex === 1 ? 10 : 0 }}>
      <div style={{ fontSize: FONT_BODY, lineHeight: LINE_HEIGHT, color: "#111", minWidth: 0 }}>
        {left.map((c, i) => (
          <ClauseBlock key={`${pageIndex}-l-${i}-${c.title}`} {...c} />
        ))}
      </div>
      <div style={{ fontSize: FONT_BODY, lineHeight: LINE_HEIGHT, color: "#111", minWidth: 0 }}>
        {right.map((c, i) => (
          <ClauseBlock key={`${pageIndex}-r-${i}-${c.title}`} {...c} />
        ))}
      </div>
    </div>

    <hr style={{ border: "none", borderTop: "1px solid #000", marginTop: 10 }} />
    <div
      className="signature-section"
      data-section={`rental_terms_page${pageIndex}_footer`}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: 11,
        marginTop: 6,
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        Initialled by Hirer:
        <span
          className="initial-field adobe-sig"
          style={{
            border: "1px solid #888",
            display: "inline-block",
            width: 70,
            height: 24,
            textAlign: "center",
            fontSize: 10,
            color: "#6b7280",
          }}
        >
         {!isSupportUser && "{{Int1_es_:signer1:initials}}"}
        </span>
      </span>
      <span>All areas have been completed and acknowledged prior to initialling this page.</span>
    </div>
  </div>
);

const TermsAndConditions = ({ isPdfMode,isSupportUser  }) => {
  const [pagesWithColumns, setPagesWithColumns] = React.useState(null);
  const measureRef = React.useRef(null);

  // Pass 1: render every clause off-screen at the real column width, then
  // read back its actual rendered height. Runs once (layout doesn't depend
  // on isPdfMode — pagination is computed once and reused for both the
  // on-screen preview and the PDF export, so what you see matches what
  // gets exported).
  React.useLayoutEffect(() => {
    if (!measureRef.current) return;
    const nodes = Array.from(measureRef.current.children);
    const withHeights = MASTER_CLAUSES.map((c, i) => ({
      ...c,
      __measuredHeight: nodes[i] ? nodes[i].offsetHeight : 0,
    }));
    setPagesWithColumns(layoutIntoPagesByHeight(withHeights, COLS_PER_PAGE, MAX_COLUMN_HEIGHT_PX));
  }, []);

  // Hidden measurement pass — same ClauseBlock component/styling used in
  // the real layout, so the measured height matches the real render.
  const measurementPass = (
    <div
      ref={measureRef}
      style={{
        position: "absolute",
        top: 0,
        left: -99999,
        width: COLUMN_WIDTH_PX,
        visibility: "hidden",
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      {MASTER_CLAUSES.map((c, i) => (
        <ClauseBlock key={`measure-${i}-${c.title}`} {...c} />
      ))}
    </div>
  );

  if (!pagesWithColumns) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-sm text-gray-400">
        {measurementPass}
        Preparing terms and conditions layout…
      </div>
    );
  }

  const allPages = (
    <>
      {pagesWithColumns.map(([left, right], i) => (
        <React.Fragment key={i}>
          <PageBlock left={left} right={right} pageIndex={i + 1} isPdfMode={isPdfMode} isSupportUser={isSupportUser} />
          {i < pagesWithColumns.length - 1 && <div style={{ height: isPdfMode ? 0 : 24 }} />}
        </React.Fragment>
      ))}
    </>
  );

  if (isPdfMode) return allPages;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
      {allPages}
    </div>
  );
};







// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 7 — GoCardless QR (last page)
// ═══════════════════════════════════════════════════════════════════════════════
const GoCardlessPage = ({ isPdfMode }) => {
  const src = "https://quoting-tool.fibretelecomsuk.co.uk/images/scanner_image.jpg";

  const inner = (
    <div style={{ width: "100%", height: isPdfMode ? "1123px" : "800px" }}>
      <img
        src={src}
        alt="GoCardless scanned document"
        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
      />
    </div>
  );

  if (isPdfMode) return <div style={{ ...pdfDocStyle, minHeight: 1123, padding: 0 }}>{inner}</div>;
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">{inner}</div>;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN — RentalAgreementTab
// ═══════════════════════════════════════════════════════════════════════════════
const RentalAgreementTab = ({ selectedConnectionId,currentUser  }) => {
  const [isPdfMode, setIsPdfMode] = useState(false);
  const isSupportUser = currentUser?.role === "Support";
  console.log("isSupportUser",currentUser);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingAdobePDF, setIsGeneratingAdobePDF] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingSettlement, setIsSavingSettlement] = useState(false);
  const [settlementMsg, setSettlementMsg] = useState(null);
  const page1Ref = useRef(null);
  const page2Ref = useRef(null);
  const page3Ref = useRef(null);
  const page4Ref = useRef(null);
  const page5Ref = useRef(null);

  const emptyRow = () => ({ postcode: "", quantity: "", description: "", newUsed: "" });

  const [cover, setCover] = useState({
    fullName: "", contactName: "", address: "", postcode: "", email: "", telephone: "", companyReg: "",
    fixedPeriodWords: "", initialRental: "", periodicRental: "", periodicRentalWords: "",
    recurringCharges: "", totalRental: "", oneOffCharges: "", invoiceBasis: "monthly",
    existingFinanceCompany: "", existingAgreementRef: "", settlementAmount: "",
    sig1Name: "", sig1JobTitle: "", sig1Date: "",
    sig2Name: "", sig2JobTitle: "", sig2Date: "",
    agreementDate: "", marketingConsent: false,
  });

  const [schedule, setSchedule] = useState({
    equipmentRows: Array(6).fill(null).map(emptyRow),
    oneOffRows: Array(6).fill(null).map(emptyRow),
    recurringRows: Array(6).fill(null).map(emptyRow),
    additionalProvisions: "",
  });

   const saveSettlement = async () => {
    if (!selectedConnectionId) return;
    try {
      const token = localStorage.getItem("authToken");
      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/rental-settlement/${selectedConnectionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            existing_finance_company: cover.existingFinanceCompany || null,
            existing_agreement_ref:   cover.existingAgreementRef || null,
            settlement_amount:        cover.settlementAmount === "" ? null : String(cover.settlementAmount),
          }),
        }
      );
    } catch (e) {
      console.error("Failed to save settlement details:", e);
    }
  };

  const findItem = (arr, name) => arr.find((i) => i.name === name) || { quantity: 0, focQuantity: 0 };
  const totalQty = (item) => (item.quantity || 0) + (item.focQuantity || 0);

  useEffect(() => {
    if (!selectedConnectionId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
         const { data } = await getSystemConfig(selectedConnectionId);
         //const { data } = "";
        if (!data) return;

        const contractDateFormatted = formatToDDMMYYYY(data.contract_date);
        const fullAddress = [
          data.registered_address_line_1, data.registered_address_line_2,
          data.registered_address_line_3, data.registered_address_line_4,
        ].filter(Boolean).join("\n");

        const lease = data.leaseCalculatorData || {};
        const adjustedRental   = lease.adjustedRental   || "";
        const newServiceCharge = lease.new_service_charges || "";
        const paymentProfile   = lease.paymentProfile   || "";

        const periodicRentalNum = parseFloat(adjustedRental) || 0;
        const recurringNum      = parseFloat(newServiceCharge) || 0;
        const totalNum          = periodicRentalNum + recurringNum;

        const hwItems     = Array.isArray(data.hardwareItems) ? data.hardwareItems : [];
        const backendAddl = data.additionalHardware
          ? Array.isArray(data.additionalHardware) ? data.additionalHardware : Object.values(data.additionalHardware)
          : [];

       const buildEquipmentRows = () => {
          const mobileAppUsers = hwItems.reduce((total, item) => {
            if (item.name === "Mobile App" )
              return total + (item.quantity || 0) + (item.focQuantity || 0);
            return total;
          }, 0);

          const t54w = findItem(hwItems, "T54W");
          const t57w = findItem(hwItems, "T57W");
          const t58w = findItem(hwItems, "T58W");
          const t88 = findItem(hwItems, "YEALINK T88");
          const t85 = findItem(hwItems, "YEALINK T85");
          const t87 = findItem(hwItems, "YEALINK T87");
          const grandstream3470 = findItem(hwItems, "Grandstream 3470");
          const grandstream3480 = findItem(hwItems, "Grandstream 3480");
          const cisco = findItem(hwItems, "Cisco 9871");
          const ipDectHandset = findItem(hwItems, "Yealink W73H DECT");
          console.log("ipDectHandset",ipDectHandset);

          const ucmpbx = findItem(backendAddl, "UCM IP PBX");
          const wifi = findItem(backendAddl, "Draytek 2865LAC");
          const poe = findItem(backendAddl, "Netgear GS108LP");
        
           

          const lines = [];
          const push = (qty, description) => {
            lines.push({
              postcode: data.postcode || "",
              quantity: String(qty),
              description,
              newUsed: "New",
            });
          };

          if (mobileAppUsers > 0) push(mobileAppUsers, "GAMMA MOBILE - MOBILE CLIENT APPLICATION");
          if (totalQty(t54w) > 0) push(totalQty(t54w), "YEALINK T54W - IP DESK PHONE HANDSET");
          if (totalQty(t57w) > 0) push(totalQty(t57w), "YEALINK T57W - IP DESK PHONE HANDSET");
          if (totalQty(t58w) > 0) push(totalQty(t58w), "YEALINK T58W - IP DESK PHONE HANDSET");
          if (totalQty(t88) > 0)  push(totalQty(t88),  "YEALINK T88 - IP DESK PHONE HANDSET");
          if (totalQty(t85) > 0)  push(totalQty(t85),  "YEALINK T85 - IP DESK PHONE HANDSET");
          if (totalQty(t87) > 0)  push(totalQty(t87),  "YEALINK T87 - IP DESK PHONE HANDSET");
          if (totalQty(grandstream3470) > 0) push(totalQty(grandstream3470), "GRANDSTREAM 3470 - IP DESK PHONE HANDSET");
          if (totalQty(grandstream3480) > 0) push(totalQty(grandstream3480), "GRANDSTREAM 3480 - IP DESK PHONE HANDSET");
          if (cisco.quantity > 0) push(cisco.quantity, "CISCO 9871 - IP DESK PHONE HANDSET");
          //if (ipDectHandset.quantity > 0)
           // push(ipDectHandset.quantity, "YEALINK Yealink W73H DECT - IP DECT CORDLESS PHONE HANDSET");
          const dectQty = totalQty(ipDectHandset);
          if (dectQty > 0) push(dectQty, "YEALINK Yealink W73H DECT - IP DECT CORDLESS PHONE HANDSET");
          if (ucmpbx.quantity > 0) push(ucmpbx.quantity, "UCM PBX - UCM IP PBX");
          if (wifi.quantity > 0)   push(wifi.quantity, "DRAYTEK 2865LAC ROUTER - Draytek 2865LAC ROUTER");
          if (poe.quantity > 0)    push(poe.quantity, "NETGEAR GS108LP SWITCH - Netgear GS108LP SWITCH");

          // Fibre installation — always present, exactly as in RentalAgreementForm
          push(1, "FIBRE INSTALLATION - PHONE SYSTEM INSTALLATION");

          // Licence / Reporting / Recording — only when there are mobile app users
          if (mobileAppUsers > 0) {
            push(mobileAppUsers, "GAMMA LICENCE - LIFETIME LICENCE");
            push(mobileAppUsers, "TOLLRING BUSINESS - CALL REPORTING");
            push(mobileAppUsers, "TOLLRING BUSINESS - CALL RECORDING");
          }

          while (lines.length < 6) lines.push(emptyRow());
          return lines;
        };

        let mechanicsData = [];
        try {
          const mResp = await fetchMechanicsData();
          mechanicsData = mResp?.data?.services || [];
        } catch (_) {}

        const getPrice = (name) => {
          const svc = mechanicsData.find((s) => s.name === name);
          return parseFloat(svc?.price || 0);
        };
        const r2 = (n) => parseFloat((Math.round(n * 100) / 100).toFixed(2));

        const cl  = data.currentLines   || {};
        const db  = data.dataBroadband  || {};
        const lr  = data.lineRequirements || {};
        const linesFoc = data.linesFoc || {}; 
        const mobileAppQty = hwItems.reduce((s, h) =>
          h.name === "Mobile App" || h.name === "Mobile App Addon" ? s + (h.quantity ?? 0) : s, 0);
        const failsafeSum = (cl.adslFttcBroadband || 0) + (cl.new4g5gBroadband || 0) + (cl.fttpSogeaBroadband || 0);

        const serviceQtyMap = [
          { name: "SOGEA",            qty: cl.fttpSogeaBroadband  || 0 },
          { name: "FTTC BB",          qty: cl.adslFttcBroadband   || 0 },
          { name: "4G BB",            qty: cl.new4g5gBroadband    || 0 },
          { name: "STARLINK",         qty: db.starlinkBroadband   || 0 },
          { name: "ANALOGUE LINE",    qty: cl.analoguePstnLines   || 0 },
          { name: "ISDN 2 CH",        qty: lr.existingIsdn2Channels || 0 },
          { name: "LEASED LINE",      qty: db.leasedLines         || 0 },
          { name: "FIBRE FAILSAFE",   qty: failsafeSum },
          { name: "FIBRE SAFE",       qty: failsafeSum > 0 ? 1 : 0 },
          { name: "FIBRE AUDIO",      qty: 1 },
          { name: "FIBRE AI",         qty: 1 },
          { name: "LICENSE MAINT",    qty: mobileAppQty },
          { name: "SIP TRUNK BUNDLE", qty: mobileAppQty },
          { name: "T58W",             qty: hwItems.find(i => i.name === "T58W")?.quantity || 0 },
          { name: "YEALINK T88",      qty: hwItems.find(i => i.name === "YEALINK T88")?.quantity || 0 },
          { name: "YEALINK T85",      qty: hwItems.find(i => i.name === "YEALINK T85")?.quantity || 0 },
          { name: "YEALINK T87",      qty: hwItems.find(i => i.name === "YEALINK T87")?.quantity || 0 },
          { name: "DECT",             qty: hwItems.find(i => i.name === "Yealink W73H DECT")?.quantity || 0 },
          { name: "GRANDSTREAM 3470", qty: hwItems.find(i => i.name === "Grandstream 3470")?.quantity || 0 },
          { name: "GRANDSTREAM 3480", qty: hwItems.find(i => i.name === "Grandstream 3480")?.quantity || 0 },
          { name: "CISCO 9871",       qty: hwItems.find(i => i.name === "Cisco 9871")?.quantity || 0 },
        ];

        const buildRecurringRows = (mechanicsData, cl, db, lr, hwItems, linesFoc) => {
          const getPrice = (serviceName) => {
            const service = mechanicsData.find((s) => s.name === serviceName);
            return parseFloat(service?.price || 0);
          };

          const roundToTwo = (num) => parseFloat((Math.round(num * 100) / 100).toFixed(2));

          const getHwTotal = (name) => {
            const item = hwItems.find((i) => i.name === name);
            return (item?.quantity || 0) + (item?.focQuantity || 0);
          };

          const getHwBillableQty = (name) => {
            const item = hwItems.find((i) => i.name === name);
            return item?.quantity || 0;
          };

          const fibre_fail_safe_sum =
            (cl.adslFttcBroadband || 0) + (cl.new4g5gBroadband || 0) + (cl.fttpSogeaBroadband || 0);

          // Display quantities (include FOC) — same as calculateAllCosts.quantities
          const quantities = {
            SOGEA: (cl.fttpSogeaBroadband || 0) + (linesFoc.fttpSogeaBroadbandFocQty || 0),
            "FTTC BB": cl.adslFttcBroadband || 0,
            "4G BB": (cl.new4g5gBroadband || 0) + (linesFoc.new4g5gBroadbandFocQty || 0),
            STARLINK: (db.starlinkBroadband || 0) + (linesFoc.starlinkBroadbandFocQty || 0),
            "ANALOGUE LINE": cl.analoguePstnLines || 0,
            "ISDN 2 CH": lr.existingIsdn2Channels || 0,
            T58W: getHwTotal("T58W"),
            T57W: getHwTotal("T57W"),
            "YEALINK T88": getHwTotal("YEALINK T88"),
            "YEALINK T85": getHwTotal("YEALINK T85"),
            "YEALINK T87": getHwTotal("YEALINK T87"),
            "GRANDSTREAM 3470": getHwTotal("Grandstream 3470"),
            "GRANDSTREAM 3480": getHwTotal("Grandstream 3480"),
            "CISCO 9871": getHwTotal("Cisco 9871"),
            DECT: getHwTotal("Yealink W73H DECT"),
            "LICENSE MAINT": getHwTotal("Mobile App"),
            "SIP TRUNK BUNDLE": getHwTotal("Mobile App"),
            "LEASED LINE": (db.leasedLines || 0) + (linesFoc.leasedLinesFocQty || 0),
            "FIBRE FAILSAFE": fibre_fail_safe_sum,
            "FIBRE SAFE": 1,
            "FIBRE AUDIO": getHwTotal("Fibre Audio"),
            "FIBRE AI": 1,
          };

          // Billable quantities (exclude FOC) — same as calculateAllCosts.billableQuantities
          const billableQuantities = {
            SOGEA: Math.max(0, (cl.fttpSogeaBroadband || 0) - (linesFoc.fttpSogeaBroadbandFocQty || 0)),
            "FTTC BB": cl.adslFttcBroadband || 0,
            "4G BB": Math.max(0, (cl.new4g5gBroadband || 0) - (linesFoc.new4g5gBroadbandFocQty || 0)),
            STARLINK: Math.max(0, (db.starlinkBroadband || 0) - (linesFoc.starlinkBroadbandFocQty || 0)),
            "ANALOGUE LINE": cl.analoguePstnLines || 0,
            "ISDN 2 CH": lr.existingIsdn2Channels || 0,
            T58W: getHwBillableQty("T58W"),
            T57W: getHwBillableQty("T57W"),
            "YEALINK T88": getHwBillableQty("YEALINK T88"),
            "YEALINK T85": getHwBillableQty("YEALINK T85"),
            "YEALINK T87": getHwBillableQty("YEALINK T87"),
            "GRANDSTREAM 3470": getHwBillableQty("Grandstream 3470"),
            "GRANDSTREAM 3480": getHwBillableQty("Grandstream 3480"),
            "CISCO 9871": getHwBillableQty("Cisco 9871"),
            DECT: getHwBillableQty("Yealink W73H DECT"),
            "LICENSE MAINT": getHwBillableQty("Mobile App"),
            "SIP TRUNK BUNDLE": getHwBillableQty("Mobile App"),
            "LEASED LINE": Math.max(0, (db.leasedLines || 0) - (linesFoc.leasedLinesFocQty || 0)),
            "FIBRE FAILSAFE": fibre_fail_safe_sum,
            "FIBRE SAFE": 1,
            "FIBRE AUDIO": getHwBillableQty("Fibre Audio"),
            "FIBRE AI": 1,
          };

        const rows = Object.entries(quantities)
              // Fibre Audio is a flat-fee add-on and should always appear, even
              // when its hardware quantity is 0 — mirrors the "FIBRE AUDIO"
              // special case in FibreContractForm's calculateAllCosts.
              .filter(([name, qty]) => qty > 0 || name === "FIBRE AUDIO")
              .map(([name, qty]) => {
                const isFibreAudio = name === "FIBRE AUDIO";
                const price = getPrice(name);
                const billable = billableQuantities[name];

                // Default quantity to 1 for the flat-fee addon, and base the rate
                // on the FOC-inclusive quantity rather than billable — same as
                // calculateAllCosts' effectiveQty/rate logic for FIBRE AUDIO.
                const effectiveQty = isFibreAudio ? (qty || 1) : qty;

                const newUsed = isFibreAudio
                  ? roundToTwo(effectiveQty > 1 ? effectiveQty + price : price).toFixed(2)
                  : roundToTwo(billable * price).toFixed(2);

                return {
                  postcode: data.postcode || "",
                  quantity: String(effectiveQty),
                  description: name,
                  newUsed,
                };
              });

            while (rows.length < 6) rows.push(emptyRow());
            return rows;
        };

          // ─── Number → words (currency) ────────────────────────────────────────────────
            const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
              "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
            const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

            const threeDigitsToWords = (n) => {
              let str = "";
              if (n >= 100) {
                str += `${ONES[Math.floor(n / 100)]} Hundred`;
                n %= 100;
                if (n > 0) str += " and ";
              }
              if (n >= 20) {
                str += TENS[Math.floor(n / 10)];
                if (n % 10 > 0) str += `-${ONES[n % 10]}`;
              } else if (n > 0) {
                str += ONES[n];
              }
              return str.trim();
            };

            const integerToWords = (num) => {
              if (num === 0) return "Zero";
              const units = ["", "Thousand", "Million", "Billion"];
              let unitIndex = 0;
              let n = num;
              const parts = [];
              while (n > 0) {
                const chunk = n % 1000;
                if (chunk > 0) {
                  const chunkWords = threeDigitsToWords(chunk) + (units[unitIndex] ? ` ${units[unitIndex]}` : "");
                  parts.unshift(chunkWords);
                }
                n = Math.floor(n / 1000);
                unitIndex += 1;
              }
              return parts.join(" ").trim();
            };

         // Converts a numeric £ amount into words, e.g. 122.45 -> "One Hundred and Twenty Two Pounds and Forty Five Pence"
            const amountToWords = (amount) => {
              const safeAmount = Math.max(0, amount || 0);
              const pounds = Math.floor(safeAmount);
              const pence = Math.round((safeAmount - pounds) * 100);
              const poundsLabel = pounds === 1 ? "Pound" : "Pounds";
              let result = `${integerToWords(pounds)} ${poundsLabel}`;
              if (pence > 0) {
                const penceLabel = pence === 1 ? "Penny" : "Pence";
                result += ` and ${integerToWords(pence)} ${penceLabel}`;
              }
              return result;
            };
            // Generic version of termToWords for any whole number, formatted the same way
          // e.g. numberToWordsWithDigits(11) -> "Eleven (11)"
          const numberToWordsWithDigits = (n) => {
            const num = Math.max(0, parseInt(n, 10) || 0);
            return `${integerToWords(num)} (${num})`;
          };

        setCover((prev) => ({
          ...prev,
          fullName: data.connection_name || prev.fullName,
          contactName: data.md_name || prev.contactName,
          address: fullAddress || prev.address,
          postcode: data.postcode || prev.postcode,
          email: data.director_email || data.billing_email || prev.email,
          telephone: data.telephone || prev.telephone,
          companyReg: data.company_reg || prev.companyReg,
          fixedPeriodWords: termToWords(paymentProfile),
          periodicRental: periodicRentalNum ? periodicRentalNum.toFixed(2) : prev.periodicRental,
          periodicRentalWords: paymentProfile
  ? numberToWordsWithDigits((parseInt(paymentProfile, 10) || 0) - 1)
  : prev.periodicRentalWords,
          recurringCharges: recurringNum ? recurringNum.toFixed(2) : prev.recurringCharges,
          totalRental: totalNum ? totalNum.toFixed(2) : prev.totalRental,
          initialRental: periodicRentalNum ? periodicRentalNum.toFixed(2) : prev.initialRental,
          oneOffCharges: "",
          invoiceBasis: "monthly",
                   existingFinanceCompany: "", existingAgreementRef: "", settlementAmount: "",
          agreementDate: contractDateFormatted,
          sig1Name: data.md_name || prev.sig1Name,
          sig1JobTitle: data.position || prev.sig1JobTitle,
          sig1Date: contractDateFormatted,
        }));

        setSchedule((prev) => ({
          ...prev,
          equipmentRows: buildEquipmentRows(),
          recurringRows: buildRecurringRows(mechanicsData, cl, db, lr, hwItems, linesFoc),
          additionalProvisions: data.additional_provisions || prev.additionalProvisions,
        }));

                try {
          const token = localStorage.getItem("authToken");
          const sRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/rental-settlement/${selectedConnectionId}`,
            { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
          );
          const sJson = await sRes.json();
          if (sJson?.data) {
            setCover((prev) => ({
              ...prev,
              existingFinanceCompany: sJson.data.existing_finance_company || "",
              existingAgreementRef:   sJson.data.existing_agreement_ref   || "",
              settlementAmount:       sJson.data.settlement_amount != null ? String(sJson.data.settlement_amount) : "",
            }));
          }
        } catch (e) {
          console.error("Failed to load settlement details:", e);
        }

      } catch (err) {
        console.error("RentalAgreementTab: failed to load config", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedConnectionId]);

  const patchCover    = (k, v) => setCover((p) => ({ ...p, [k]: v }));
  const patchSchedule = (k, v) => setSchedule((p) => ({ ...p, [k]: v }));

  // Helper: turn signature-section/initial-field placeholders into locked
  // preview inputs before rasterising for a plain PDF download (keeps the
  // download looking consistent with the Adobe version without needing a
  // live Adobe session).
  const addFormFieldsToSignatures = (containerEl) => {
    const signatureSections = containerEl.querySelectorAll(".signature-section");
    signatureSections.forEach((section, index) => {
      const sectionId = section.getAttribute("data-section") || `section_${index + 1}`;

      const signatureFields = section.querySelectorAll(".signature-field");
      signatureFields.forEach((field, fieldIndex) => {
        if (field.querySelector("input")) return;
        const input = document.createElement("input");
        input.type = "text";
        input.name = `${sectionId}_signature_${fieldIndex + 1}`;
        input.className = "adobe-sign-field signature-input";
        input.setAttribute("data-adobe-field-type", "signature");
        input.style.width = "100%";
        input.style.height = "100%";
        input.style.border = "1px solid #666";
        input.style.background = "transparent";
        input.placeholder = "Signature";
        input.readOnly = true;
        field.innerHTML = "";
        field.appendChild(input);
      });

      const initialFields = section.querySelectorAll(".initial-field");
      initialFields.forEach((field, fieldIndex) => {
        if (field.querySelector("input")) return;
        const fieldName = field.getAttribute("data-field-name") || `${sectionId}_initial_${fieldIndex + 1}`;
        const input = document.createElement("input");
        input.type = "text";
        input.name = fieldName;
        input.className = "adobe-sign-field initial-input";
        input.setAttribute("data-adobe-field-type", "initial");
        input.style.width = "100%";
        input.style.height = "100%";
        input.style.border = "1px solid #666";
        input.style.background = "transparent";
        input.style.textAlign = "center";
        input.placeholder = "Initial";
        input.readOnly = true;
        field.innerHTML = "";
        field.appendChild(input);
      });
    });
  };

const handleDownloadPdf = async () => {
  await saveSettlement();
  setIsDownloading(true);
  setIsPdfMode(true);
  await new Promise((r) => setTimeout(r, 500));

  try {
    // ── Build main content (pages 1–4) as one stitched container ──────────
    const docRefs = [page1Ref, page2Ref, page3Ref, page4Ref];
    const container = document.createElement("div");
    container.style.background = "#ffffff";

    docRefs.forEach((ref, index) => {
      if (!ref.current) return;
      const clone = ref.current.cloneNode(true);
      clone.querySelectorAll(".no-pdf-export").forEach((el) => el.remove());
      if (index > 0) {
        clone.style.pageBreakBefore = "always";
        clone.style.breakBefore = "page";
      }
      container.appendChild(clone);
    });

    const options = {
      margin: [10, 10, 10, 10],
      filename: `rental-agreement-${selectedConnectionId || "draft"}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
        hotfixes: ["px_scaling"],
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    // Get the main document as raw bytes so we can merge in the scanner PDF
    const mainPdfArrayBuffer = await html2pdf()
      .set(options)
      .from(container)
      .outputPdf("arraybuffer");

    // ── Merge scannerimage.pdf pages at the end ────────────────────────────
    const mainDoc = await PDFDocument.load(mainPdfArrayBuffer);

    const scannerRes = await fetch("/scannerimage.pdf");
    const scannerBytes = await scannerRes.arrayBuffer();
    const scannerDoc = await PDFDocument.load(scannerBytes);

    const copiedPages = await mainDoc.copyPages(scannerDoc, scannerDoc.getPageIndices());
    copiedPages.forEach((page) => mainDoc.addPage(page));

    const mergedBytes = await mainDoc.save();
    const blob = new Blob([mergedBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rental-agreement-${selectedConnectionId || "draft"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("PDF download failed:", err);
    alert("Error generating PDF. Please try again.");
  } finally {
    setIsPdfMode(false);
    setIsDownloading(false);
  }
};

  const handleSend = async () => {
    setIsSending(true);
    setSendStatus(null);
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 300));
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("authToken");
      const docRefs = [page1Ref, page2Ref, page3Ref, page4Ref, page5Ref];
      const combinedHtml = docRefs.map((ref) => ref.current?.innerHTML || "").join('<div style="page-break-before:always"></div>');
      const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><script src="https://cdn.tailwindcss.com"></script><style>body{font-family:Arial,Helvetica,sans-serif;color:#111;background:#fff;}</style></head><body>${combinedHtml}</body></html>`;
      const res = await fetch(`${API_BASE_URL}/rental-agreement/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ connection_id: selectedConnectionId, html: htmlContent, cover, schedule }),
      });
      if (res.ok) {
        setSendStatus({ type: "success", message: "Rental agreement sent successfully." });
      } else {
        const err = await res.json().catch(() => ({}));
        setSendStatus({ type: "error", message: err?.message || "Failed to send. Please try again." });
      }
    } catch {
      setSendStatus({ type: "error", message: "Network error. Please check your connection." });
    } finally {
      setIsPdfMode(false);
      setIsSending(false);
    }
  };

  // ── Adobe e-Signature ──────────────────────────────────────────────────
  // Mirrors FibreContractForm's sendToAdobeSign: builds the full agreement
  // HTML (with the {{Sig..._es_:signer...:signature}} / {{Int..._es_:signer...:initials}}
  // text tags left intact so Adobe Sign can detect and convert them into
  // real signature/initial fields), then posts it to the same
  // /pdf/generate-and-send endpoint used by the main contract.
  const sendToAdobeSign = async () => {
    if (!cover.email) {
      alert("Please enter a customer email address on Page 1 before sending for signature.");
      return;
    }
    if (!window.confirm(`Send the rental agreement to: ${cover.email}\n\nDo you want to continue?`)) return;
    await saveSettlement();
    setIsGeneratingAdobePDF(true);
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 300));

    try {
      const docRefs = [page1Ref, page2Ref, page3Ref, page4Ref];
      const container = document.createElement("div");
      container.style.background = "#ffffff";

      docRefs.forEach((ref, index) => {
        if (!ref.current) return;
        const clone = ref.current.cloneNode(true);
        clone.querySelectorAll(".no-pdf-export").forEach((el) => el.remove());
        if (index > 0) {
          clone.style.pageBreakBefore = "always";
          clone.style.breakBefore = "page";
        }
        container.appendChild(clone);
      });

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${container.innerHTML}
        </body>
        </html>
      `;

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      const body = {
        html: htmlContent,
        signer_email: cover.email,
        signer_name: cover.contactName || cover.fullName,
        agreement_name: `Fibre Telecoms Rental Agreement - ${cover.fullName || "Customer"}`,
        email_subject: `Fibre Telecoms Rental Agreement for ${cover.fullName || "your company"}`,
        email_body: `Dear ${cover.contactName || "Customer"},\n\nPlease review and sign the attached Phone System Rental Agreement.\n\nThank you.`,
        connection_id: selectedConnectionId,
        has_mobile_data: 0,
        has_rental: 1,
        sandbox: true,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/pdf/generate-and-send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      );

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error("Non-JSON response received:", textResponse.substring(0, 500));
        throw new Error(`Server returned non-JSON response (Status ${response.status}).`);
      }

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || "Failed to send rental agreement");
      }

      setSendStatus({ type: "success", message: "Rental agreement sent for Adobe e-signature." });
    } catch (error) {
      console.error("Adobe Send Error (Rental Agreement):", error);
      setSendStatus({ type: "error", message: `Error sending for signature: ${error.message}` });
    } finally {
      setIsGeneratingAdobePDF(false);
      setIsPdfMode(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <svg className="animate-spin h-6 w-6 mr-3 text-blue-600" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading rental agreement data…
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-6">
       {!isPdfMode && (
        <div className="flex justify-end gap-3 pt-2 pb-8">
          
          <button onClick={handleDownloadPdf} disabled={isDownloading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${isDownloading ? "border-gray-200 text-gray-400 cursor-not-allowed" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}>
            <Download className="h-4 w-4" />
            {isDownloading ? "Generating PDF…" : "Download PDF"}
          </button>
          {!isSupportUser && (
            <button onClick={sendToAdobeSign} disabled={isGeneratingAdobePDF}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${isGeneratingAdobePDF ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 active:bg-green-800"}`}>
              <Send className="h-4 w-4" />
              {isGeneratingAdobePDF ? "Sending…" : "Adobe e-Signature"}
            </button>
          )}
          
        </div>
      )}
      {!isPdfMode && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
            <FileText className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">Phone System Rental Agreement</h4>
            <p className="text-xs text-blue-500">Fields auto-populated from the installation record. Edit any field below if needed, then download, send, or request an Adobe e-signature.</p>
          </div>
        </div>
      )}

      <div>
        {!isPdfMode && <SectionDivider title="Page 1 — Customer Details & Agreement Terms" />}
        <div ref={page1Ref}>
          <CoverImagePage data={cover} onChange={patchCover} isPdfMode={isPdfMode} />
          <div
            style={
              isPdfMode
                ? { pageBreakBefore: "always", breakBefore: "page" }
                : undefined
            }
          >
            <CoverPage data={cover} onChange={patchCover} isPdfMode={isPdfMode} isSupportUser={isSupportUser}/>
          </div>
        </div>

        {!isPdfMode && <SectionDivider title="" />}
        <div ref={page2Ref}>
          <EquipmentSchedule data={schedule} onChange={patchSchedule} isPdfMode={isPdfMode} isSupportUser={isSupportUser}/>
        </div>

        {!isPdfMode && <SectionDivider title="Pages 3–5 — Terms and Conditions" />}
        <div ref={page3Ref}>
          <TermsAndConditions isPdfMode={isPdfMode} isSupportUser={isSupportUser}/>
        </div>

        

        {!isPdfMode && <SectionDivider title="Page 7 — GoCardless Direct Debit" />}
        <div ref={page5Ref}>
          <GoCardlessPage isPdfMode={isPdfMode} />
        </div>
      </div>

      {sendStatus && !isPdfMode && (
        <div className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${sendStatus.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
          <span className="text-lg leading-none shrink-0">{sendStatus.type === "success" ? "✓" : "✕"}</span>
          <p>{sendStatus.message}</p>
        </div>
      )}

      {!isPdfMode && (
        <div className="flex justify-end gap-3 pt-2 pb-8">
          
          <button onClick={handleDownloadPdf} disabled={isDownloading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${isDownloading ? "border-gray-200 text-gray-400 cursor-not-allowed" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}>
            <Download className="h-4 w-4" />
            {isDownloading ? "Generating PDF…" : "Download PDF"}
          </button>
          {!isSupportUser && (
                  <button onClick={sendToAdobeSign} disabled={isGeneratingAdobePDF}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${isGeneratingAdobePDF ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 active:bg-green-800"}`}>
                    <Send className="h-4 w-4" />
                    {isGeneratingAdobePDF ? "Sending…" : "Adobe e-Signature"}
                  </button>
          )}
          
        </div>
      )}
    </div>
  );
};

export default RentalAgreementTab;